/* =============================================================================
   GOOFY TAILS — PET PLAN QUIZ  (assets/gt-pet-quiz.js)
   -----------------------------------------------------------------------------
   Version : 17 Oct 2026 — review fixes 1-12 + cart drawer, formatted for editing
   Loaded by: sections/gt-pet-quiz.liquid (needs <div id="quiz-root"> on the page)
   Styles  : assets/gt-pet-quiz.css (every rule scoped under .gtq)
   Data in : window.GTQ  (built by the section from the "Pet plan quiz" collection
             + section settings in the theme editor)

   HOW TO FIND THINGS — press Cmd+F and search for the tag in capitals:
     SECTION 1  CONFIG ............ settings coming from the theme editor
     SECTION 2  PRODUCTS (P) ...... every product, allergens, ingredients
     SECTION 3  LIVE SHOPIFY DATA . prices, variants, stock, photos, pack choice
     SECTION 4  FEEDING AMOUNTS ... DOSE + RULES tables
     SECTION 5  GOALS -> PRODUCTS . CONCERNS + EXTRAS ("Also good for")
     SECTION 6  BREEDS / WEIGHTS .. BREEDS, WBANDS, JOINT_BREEDS
     SECTION 7  ALLERGY / HEALTH .. FLAGS (questions) + FAQ snippets
     SECTION 8  RULES ENGINE ...... recommend(): exclusions, scoring, picks
     SECTION 9  DOSE TEXT ......... doseLine(), catMeal(), mealPlan(), variantFor()
     SECTION 10 QUIZ QUESTIONS .... STEPS[] (one object per screen)
     SECTION 11 SCREEN RENDERING .. render(), next(), breed search, "Not listed" box
     SECTION 12 SCIENCE SCREENS ... SCIENCE copy for the plan-reveal screen
     SECTION 13 WHATSAPP UNLOCK ... renderStory(), waCheck(), sendLead()
     SECTION 14 RESULTS + CART .... renderResult(), bundle total, addAll()
     SECTION 15 SAVE / START ...... localStorage resume + start-up

   COMMON CHANGES (recipe -> where):
     • Price / stock / photo .......... NOT here. Change the product in Shopify admin;
                                         the quiz reads them live from the collection.
     • Add a new product .............. 1) add it to the "Pet plan quiz" collection
                                         2) add an entry in SECTION 2 (P) with its handle
                                         3) route it to goals in SECTION 5 (CONCERNS)
                                         4) pick its pack sizes in SECTION 3 (VSEL)
     • Product allergens/ingredients .. SECTION 2 "contains" + "ing". Admin can only ADD
                                         allergens via metafields custom.quiz_allergens
                                         / custom.quiz_ingredients (never remove — safety).
     • Which products a goal suggests . SECTION 5 CONCERNS (order = priority)
     • Feeding / dose amounts ......... SECTION 4 (keep in sync with data/feeding-rules.json)
     • Question wording ............... SECTION 10 STEPS
     • Discount % / code / webhook .... theme editor > Pet plan quiz section (not code)
     • After add-to-cart behaviour .... SECTION 14 addAll() + "Stay on page" setting

   EVENTS this file fires on document (for apps / analytics / WhatsApp tools):
     gtq:lead      {type:'unlock'|'back-in-stock', phone, consent, pet, species, breed,
                    concerns, plan, other, product, leadId}
     gtq:added     {items:[{id, quantity, properties}]}   after cart add succeeds
     gtq:newbreed  {species, breed}                       customer typed an unlisted breed

   SAFETY RULES — please keep:
     • Allergen / medical exclusions run in plain code (SECTION 8). No AI decides them.
     • Products with no published ingredient list are never shown to allergic pets.
     • The phone number is never saved in the browser or put in a URL.
     • Escape any customer-typed text with esc() before putting it in HTML.

   BEFORE PUBLISHING A CHANGE: test in the DRAFT theme first (dog + cat path, chicken
   allergy, kidney, add to cart, 375 px mobile width). Keep a copy of the old file.
   Note: this file was originally generated from src/quiz.html by
   scripts/build-shopify.py — if that build is used again, copy these changes there.
   ============================================================================= */
(function () {
  'use strict';
  if (!document.getElementById('quiz-root')) return;

  /* =====================================================================
   DATA — every product, dose and reason below is taken from the live
   goofytails.com PDP + FAQ copy (scraped 13 Oct 2026). Edit here only.
   ===================================================================== */
  /* ============================================================================
     SECTION 1 — CONFIG (window.GTQ)
     Filled in by sections/gt-pet-quiz.liquid from the theme-editor settings:
     shopify, cartUrl, stayOnPage, freeShipping, unlock, unlockPct, discountCode,
     leadEndpoint (WhatsApp webhook), showDrafts, petBase, products (live data).
     Change these in Customize > Pet plan quiz section, not in this file.
     ============================================================================ */
  /* GTQ: set by the Shopify section (sections/gt-pet-quiz.liquid). Absent in the standalone demo. */
  const GTQ = window.GTQ || {};
  const SITE = GTQ.shopify ? '/products/' : 'https://goofytails.com/products/';
  if (GTQ.shopify && !Object.keys(GTQ.products || {}).length) {
    document.getElementById('quiz-root').innerHTML =
      '<p>The pet plan quiz is being set up. Please check back soon.</p>';
    console.warn('[GTQ] Quiz collection is empty or not found');
    return;
  }

  /* ============================================================================
     SECTION 2 — PRODUCTS (P)
     One entry per quiz product. Key = short id used everywhere (e.g. dBroth).
     Fields: sp dog|cat|both · fmt broth|supplement|meal|treat|care|kit · h = Shopify handle
     contains = allergens [chicken,fish,egg,dairy,lamb,buff,gluten,grain,soy]
     ing = ingredient text (also scanned for allergen words) · unverified = no full list
     broth/phos/sugar/fatty = medical flags used by recommend() · minMonths = min age
     price/unit here are FALLBACKS only — live Shopify prices win.
     ============================================================================ */
  /* contains: confirmed allergens from the published ingredient list.
   unverified: the PDP does not publish a full ingredient list (or names
   "bone broth" without its source), so we cannot promise it is free of an
   allergen. Unverified products are never recommended to an allergic pet. */
  const P = {
    // ---------- DOG ----------
    dBroth: {
      sp: 'dog',
      fmt: 'broth',
      name: 'Chicken Bone Broth',
      short: 'Chicken Broth',
      price: 302,
      unit: '3 × 100 ml',
      bulk: { price: 1431, unit: '15 × 100 ml' },
      h: 'goofy-tails-bone-broth-for-dogs-and-puppies',
      contains: ['chicken'],
      broth: 1,
      ing: 'Chicken bones, apple cider vinegar, water (ingredient panel on product page)',
    },
    dLamb: {
      sp: 'dog',
      fmt: 'broth',
      name: 'Lamb Bone Broth',
      short: 'Lamb Broth',
      price: 313,
      unit: '3 × 100 ml',
      h: 'goofy-fresh-lamb-bone-broth-for-dogs-and-puppies-100ml',
      contains: ['lamb'],
      broth: 1,
      ing: 'Lamb bones, apple cider vinegar, water (product page; lamb-only confirmed by Goofy Tails 13 Oct)',
    },
    dKit: {
      sp: 'dog',
      fmt: 'kit',
      name: 'Hydration Kit for Dogs',
      short: 'Hydration Kit',
      price: 747,
      unit: 'Broth + Canine Revive+',
      h: 'hydration-kit-for-dogs',
      contains: ['chicken'],
      broth: 1,
      sugar: 1,
      ing: 'Chicken bone broth + Canine Revive+ (natural chicken bone broth, collagen peptides, inulin, dextrose, maltodextrin)',
    },
    dRevive: {
      sp: 'dog',
      fmt: 'supplement',
      name: 'Canine Revive+',
      short: 'Revive+',
      price: 250,
      unit: '100 ml pack',
      bulk: { price: 1125, unit: '5 × 100 ml' },
      h: 'dog-broth-canine-revive',
      contains: ['chicken'],
      broth: 1,
      sugar: 1,
      dose: 'dogStd',
      packMl: 100,
      ing: 'Natural chicken bone broth, collagen peptides, soluble dietary fibre (inulin), dextrose, maltodextrin',
    },
    dVital: {
      sp: 'dog',
      fmt: 'supplement',
      name: 'Canine Vitality+',
      short: 'Vitality+',
      price: 180,
      unit: '100 ml pack',
      bulk: { price: 900, unit: '5 × 100 ml' },
      h: 'dog-broth-canine-vitality',
      contains: ['chicken'],
      broth: 1,
      dose: 'dogStd',
      packMl: 100,
      ing: 'Natural chicken bone broth, collagen peptides, ashwagandha root extract, turmeric curcumin extract, soluble fenugreek dietary fibre, boswellia extract',
    },
    dMob: {
      sp: 'dog',
      fmt: 'supplement',
      name: 'Canine Mobility+',
      short: 'Mobility+',
      price: 900,
      unit: '15 × 25 ml sachets',
      bulk: { price: 1710, unit: '2 boxes of 15 × 25 ml' },
      large: {
        price: 1650,
        unit: '15 × 50 ml sachets',
        bulk: { price: 2185, unit: '2 boxes of 15 × 50 ml' },
      },
      smallOOS: 1,
      h: 'goofy-tails-canine-mobility-15-packer',
      contains: ['chicken', 'fish'],
      broth: 1,
      dose: 'mobility',
      ing: 'Natural chicken bone broth, glucosamine HCl (shellfish source), Opti MSM, collagen I/II/III, chondroitin, curcuminoids, boswellia, hyaluronic acid, EPA/DHA, green-lipped mussel, vitamins C and E',
    },
    dTopper: {
      sp: 'dog',
      fmt: 'meal',
      name: 'Chicken Super Food Topper',
      short: 'Topper',
      price: 299,
      unit: '4 × 70 g pouches',
      h: 'goofy-tails-chicken-super-food-topper-for-dogs-puppies',
      contains: ['chicken'],
      unverified: 1,
      meal: 1,
      topper: 1,
    },
    dQuinoa: {
      sp: 'dog',
      fmt: 'meal',
      name: 'Chicken & Quinoa Wholesome Meal',
      short: 'Chicken & Quinoa',
      price: 106,
      unit: '100 g meal',
      sizes: { 100: [106, 705, 1431], 200: [200, 1330, 2700], 300: [300, 1995, 0] },
      h: 'goofy-tails-goofilicious-chicken-fresh-food-for-dogs-and-puppies-wet-dog-food-cooked-in-chicken-bone-broth-200-g-ready-to-eat-with-human-grade-ingredients-preservative-free',
      contains: ['chicken', 'egg'],
      meal: 1,
      phos: 1,
      ing: 'Chicken breast, whole chicken, chicken liver, eggs, rice, quinoa, chia seeds, coconut oil, carrot, beetroot, spinach, fresh mint, chicken bone broth',
    },
    dHerbs: {
      sp: 'dog',
      fmt: 'meal',
      name: 'Chicken & Herbs Grain-Free Meal',
      short: 'Chicken & Herbs',
      price: 106,
      unit: '100 g meal',
      sizes: { 100: [106, 705, 1431] },
      h: 'goofy-tails-chicken-and-herbs-gluten-grain-free-food-for-dogs-and-puppies',
      contains: ['chicken', 'egg'],
      fatty: 1,
      meal: 1,
      phos: 1,
      ing: 'Chicken liver, roasted chicken skin, bone broth, eggs, pumpkin, sweet potato, hemp seed, spinach, coconut flakes, banana, egg shell powder, basil, vitamin premix',
    },
    dLambR: {
      sp: 'dog',
      fmt: 'meal',
      name: 'Lamb & Rosemary Grain-Free Meal',
      short: 'Lamb & Rosemary',
      price: 106,
      unit: '100 g meal',
      sizes: { 100: [106, 705, 1431] },
      h: 'goofy-tails-lamb-and-rosemary-gluten-grain-free-food-for-dogs-and-puppies',
      contains: ['lamb', 'egg'],
      meal: 1,
      noChicken: 1,
      phos: 1,
      ing: 'Lamb kidney, lamb heart, lamb liver, lamb gizzards, pea, rosemary, sweet potato, pumpkin, coconut flakes, sesame, egg shell powder, spinach, carrot, lamb bone broth, vitamin premix',
    },
    dBuff: {
      sp: 'dog',
      fmt: 'meal',
      name: 'Buff & Berry Fresh Meal',
      short: 'Buff & Berry',
      price: 200,
      unit: '200 g meal',
      sizes: { 200: [200, 1330, 2700], 300: [300, 1995, 4050] },
      h: 'goofy-tails-buff-berry-fresh-food-for-dogs-and-puppies-200g',
      contains: ['buff'],
      meal: 1,
      noChicken: 1,
      phos: 1,
      ing: 'Buff meat, buff liver, carrot, pumpkin, spinach, mixed berries, coconut oil, sunflower seeds, chia seeds, buff stock',
    },
    dTrial: {
      sp: 'dog',
      fmt: 'meal',
      name: 'Wholesome Meals Trial Pack',
      short: 'Trial Pack',
      price: 400,
      unit: '4 flavours + 25 ml broth',
      h: 'wholesome-meals-trial-pack-4-flavours-25ml-chicken-bone-broth',
      contains: ['chicken', 'egg', 'lamb', 'grain'],
      unverified: 1,
      broth: 1,
      meal: 1,
    },
    dDental: {
      sp: 'dog',
      fmt: 'treat',
      name: 'Active Dental Sticks',
      short: 'Dental Sticks',
      price: 375,
      unit: '220 g',
      h: 'goofy-tails-active-dental-sticks-dog-treats-non-vegetarian-dental-sticks-for-dogs-carrot-yam-220g',
      contains: ['dairy'],
      minMonths: 4,
      rule: 'dental',
      ing: 'Potato starch, goat milk powder, sodium caseinate, glycerin, oat fibres, carrot, yam',
    },
    dDentalV: {
      vague: ['grain', 'gluten', 'soy'],
      sp: 'dog',
      fmt: 'treat',
      name: 'Vegetarian Seaweed Dental Sticks',
      short: 'Seaweed Sticks',
      price: 375,
      unit: '24 sticks',
      h: 'copy-of-goofy-tails-dental-brushes-dog-dental-treats-and-chews-dental-treats-for-dogs-150g-non-veg',
      contains: ['dairy'],
      minMonths: 4,
      rule: 'dental',
      ing: 'Whey protein, small peptide protein, dietary fibres, chlorophyll, seaweed, glycerin, magnesium stearate, compound yeast, sodium caseinate, monostearin, essential oils',
    },
    dLiver: {
      sp: 'dog',
      fmt: 'treat',
      name: 'Freeze-Dried Chicken Liver Cubes',
      short: 'Liver Cubes',
      price: 395,
      unit: '50 g',
      h: 'goofy-tails-freeze-dried-chicken-liver-cubes-for-dogs-50g',
      contains: ['chicken'],
      rule: 'dogCubes',
      minMonths: 3,
      ing: '100% chicken liver, freeze-dried (product FAQ)',
    },
    dTwist: {
      sp: 'dog',
      fmt: 'treat',
      name: 'Active Chicken Twists',
      short: 'Chicken Twists',
      price: 280,
      unit: '100 g',
      h: 'goofy-tails-chicken-twist-100g',
      contains: ['chicken'],
      minMonths: 4,
      ing: 'Potato starch, gelatine, powdered cellulose, glycerin, chicken meal, dried parsley powder',
    },
    dPaw: {
      sp: 'dog',
      fmt: 'care',
      name: 'Hemp Paw Cream for Dogs',
      short: 'Paw Cream',
      price: 285,
      unit: '30 g balm',
      h: 'goofy-tails-hemp-paw-cream-for-dogs-and-puppies',
      contains: [],
    },
    // ---------- CAT ----------
    cBroth15: {
      sp: 'cat',
      fmt: 'broth',
      name: 'Chicken Bone Broth Shots',
      short: 'Broth Shots',
      price: 525,
      unit: '15 × 25 ml',
      packMl: 375,
      h: 'chicken-bone-broth-for-cats-and-kittens-15-pack',
      contains: ['chicken'],
      broth: 1,
      best: 1,
      minMonths: 4,
      ing: 'Chicken bones, apple cider vinegar, water',
    },
    cBroth: {
      sp: 'cat',
      fmt: 'broth',
      name: 'Chicken Bone Broth for Cats',
      short: 'Chicken Broth',
      price: 302,
      unit: '3 × 100 ml',
      packMl: 300,
      minMonths: 4,
      bulk: { price: 1431, unit: '15 × 100 ml' },
      h: 'goofy-fresh-chicken-bone-broth-for-cats-and-kittens-100ml',
      contains: ['chicken'],
      broth: 1,
      ing: 'Chicken bones, apple cider vinegar, water',
    },
    cSea: {
      sp: 'cat',
      fmt: 'broth',
      name: 'Seafood Bone Broth for Cats',
      short: 'Seafood Broth',
      price: 302,
      unit: '3 × 100 ml',
      h: 'goofy-fresh-sea-food-bone-broth-for-cats-and-kittens-100ml-buy-3-get-1-free',
      contains: ['fish'],
      broth: 1,
      minMonths: 4,
      ing: 'Seafood bone broth. Goofy Tails confirmed 14 Oct that it is made without poultry',
    },
    cKit: {
      sp: 'cat',
      fmt: 'kit',
      name: 'Hydration Kit for Cats',
      short: 'Hydration Kit',
      price: 530,
      unit: 'Broth + Feline Revive+',
      h: 'hydration-bag-for-cats',
      contains: ['chicken'],
      broth: 1,
      sugar: 1,
      ing: 'Chicken bone broth + Feline Revive+ (natural chicken bone broth, collagen peptides, inulin, dextrose, maltodextrin)',
    },
    cRevive: {
      sp: 'cat',
      fmt: 'supplement',
      name: 'Feline Revive+',
      short: 'Revive+',
      price: 250,
      unit: '100 ml pack',
      bulk: { price: 1125, unit: '5 × 100 ml' },
      h: 'cat-broth-feline-revive',
      contains: ['chicken'],
      broth: 1,
      sugar: 1,
      dose: 'catRevive',
      packMl: 100,
      ing: 'Natural chicken bone broth, collagen peptides, soluble dietary fibre (inulin), dextrose, maltodextrin',
    },
    cVital: {
      sp: 'cat',
      fmt: 'supplement',
      name: 'Feline Vitality+',
      short: 'Vitality+',
      price: 180,
      unit: '100 ml pack',
      bulk: { price: 900, unit: '5 × 100 ml' },
      h: 'cat-broth-feline-vitality',
      contains: ['chicken'],
      broth: 1,
      dose: 'catVital',
      packMl: 100,
      ing: 'Natural chicken bone broth, collagen peptides, ashwagandha root extract, turmeric curcumin extract, soluble fenugreek dietary fibre, boswellia extract',
    },
    cTrial: {
      sp: 'cat',
      fmt: 'meal',
      guide: 'catTrout',
      name: 'Cat Meals Trial Pack — Trout + Chicken & Mackerel',
      short: 'Meals Trial',
      price: 522,
      unit: 'trial pack',
      h: 'goofy-tails-himalayan-trout-mackerel-chicken-trail-pack-for-cats-kittens',
      contains: ['fish', 'chicken', 'egg'],
      meal: 1,
      phos: 1,
      ing: "Himalayan Trout recipe + Chicken & Mackerel recipe (whole chicken, chicken broth, Atlantic mackerel, chicken liver, egg white powder, brewer's yeast, inulin, vitamin premix, taurine, calcium carbonate, choline chloride, agar agar, locust bean gum, guar gum)",
    },
    cTrout: {
      sp: 'cat',
      fmt: 'meal',
      name: 'Himalayan Trout Wet Food',
      short: 'Himalayan Trout',
      price: 261,
      unit: '210 g box (3 × 70 g)',
      boxG: 210,
      guide: 'catTrout',
      h: 'goofy-tails-himalayan-trout-wet-cat-and-kitten-food',
      contains: ['fish', 'chicken'],
      meal: 1,
      phos: 1,
      ing: 'Himalayan trout, water, anchovies, vitamin premix, calcium carbonate, inulin, sunflower oil, taurine, guar gum, locust bean gum, agar agar, choline chloride. Goofy Tails confirmed 14 Oct that the current recipe also contains chicken',
    },
    cMackC: {
      sp: 'cat',
      fmt: 'meal',
      name: 'Mackerel & Chicken Wet Food',
      short: 'Mackerel & Chicken',
      price: 261,
      unit: '210 g box (3 × 70 g)',
      boxG: 210,
      guide: 'catTrout',
      h: 'goofy-tails-mackerel-and-chicken-wet-cat-and-kitten-food',
      contains: ['fish', 'chicken', 'egg'],
      meal: 1,
      phos: 1,
      ing: "Whole chicken, chicken broth, Atlantic mackerel, chicken liver, egg white powder, brewer's yeast, inulin, vitamin premix, taurine, calcium carbonate, choline chloride, agar agar, locust bean gum, guar gum",
    },
    cFlax: {
      sp: 'cat',
      fmt: 'meal',
      name: 'Chicken & Flaxseed Wet Food',
      short: 'Chicken & Flaxseed',
      price: 350,
      unit: '5 × 70 g meals',
      pouch: 70,
      guide: 'catWhole',
      h: 'goofy-tails-wholesome-all-natural-wet-cat-food-and-kitten-food-chicken-flaxseed',
      contains: ['chicken'],
      meal: 1,
      phos: 1,
      ing: 'Chicken meat, water, sunflower oil, seaweed extract, flaxseed powder, sodium alginate, vitamin E, turmeric powder',
    },
    cTuna: {
      sp: 'cat',
      fmt: 'meal',
      name: 'Tuna & Anchovies Wet Food',
      short: 'Tuna & Anchovies',
      price: 350,
      unit: '5 × 70 g meals',
      pouch: 70,
      guide: 'catWhole',
      h: 'goofy-tails-wholesome-all-natural-wet-cat-food-and-kitten-food-tuna-and-anchovies',
      contains: ['fish'],
      meal: 1,
      phos: 1,
      ing: 'Tuna meat, anchovies, water, sunflower oil, flaxseed, seaweed extract, sodium alginate, tocopherol',
    },
    c15: {
      sp: 'cat',
      fmt: 'meal',
      name: '15 Meal Combo Wet Food',
      short: '15 Meal Combo',
      price: 1050,
      unit: '15 × 70 g',
      pouch: 70,
      guide: 'catWhole',
      h: 'goofy-tails-wholesome-all-natural-wet-cat-food-and-kitten-food-combo',
      contains: ['fish', 'chicken'],
      meal: 1,
      phos: 1,
      ing: 'Chicken & Flaxseed, Tuna & Anchovies and Mackerel & Seaweed recipes (chicken meat, tuna, anchovies, mackerel, water, sunflower oil, flaxseed, seaweed extract, sodium alginate, vitamin E, turmeric)',
    },
    cMackS: {
      sp: 'cat',
      fmt: 'meal',
      name: 'Mackerel & Seaweed Wet Food',
      short: 'Mackerel & Seaweed',
      price: 350,
      unit: '5 × 70 g meals',
      pouch: 70,
      guide: 'catWhole',
      h: 'goofy-tails-wholesome-all-natural-wet-cat-food-and-kitten-food-mackerel-and-seaweed',
      contains: ['fish'],
      meal: 1,
      phos: 1,
      ing: 'Mackerel, water, sunflower oil, flaxseed powder, seaweed extract, sodium alginate, vitamin E, turmeric powder',
    },
    cLiver: {
      sp: 'cat',
      fmt: 'treat',
      name: 'Freeze-Dried Chicken Liver Treats',
      short: 'Liver Treats',
      price: 300,
      unit: '30 g',
      h: 'goofy-tails-freeze-dried-chicken-liver-cat-treats',
      contains: ['chicken'],
      rule: 'catCubes',
      minMonths: 3,
      ing: '100% chicken liver, freeze-dried (product FAQ)',
    },
    cShrimp: {
      sp: 'cat',
      fmt: 'treat',
      name: 'Freeze-Dried Shrimp Treats',
      short: 'Shrimp Treats',
      price: 650,
      unit: '30 g',
      h: 'goofy-tails-freeze-dried-shrimp-treats-for-cats-and-kittens',
      contains: ['fish'],
      ing: '100% shrimp, freeze-dried (confirmed by Goofy Tails 13 Oct)',
    },
    cTunaT: {
      sp: 'cat',
      fmt: 'treat',
      name: 'Freeze-Dried Tuna Chunks',
      short: 'Tuna Chunks',
      price: 300,
      unit: '30 g',
      h: 'goofy-tails-freeze-dried-tuna-chunks-cat-treats',
      contains: ['fish'],
      ing: '100% tuna, freeze-dried (confirmed by Goofy Tails 13 Oct)',
    },
    cPaw: {
      sp: 'cat',
      fmt: 'care',
      name: 'Hemp Butter Paw Balm for Cats',
      short: 'Paw Balm',
      price: 285,
      unit: '30 g balm',
      h: 'goofy-tails-hemp-butter-paw-cream-for-cats-and-kittens-30-g-balm',
      contains: [],
    },
    // ---------- BOTH ----------
    omega: {
      sp: 'both',
      fmt: 'supplement',
      name: 'Plant-Based Omega Oil (hemp seed)',
      short: 'Omega Oil',
      price: 300,
      unit: '50 ml',
      h: 'goofy-tails-hemp-seed-oil-for-dogs-and-cats-100-ml',
      contains: [],
      minMonths: 12,
      noDose: 1,
      ing: 'Hemp seed oil',
    },
    vet: {
      sp: 'both',
      fmt: 'care',
      name: 'Online Consultation with a Vet',
      short: 'Vet Consult',
      price: 500,
      unit: 'one consult',
      h: 'online-consultation-with-vet',
      contains: [],
    },
  };
  /* ============================================================================
     SECTION 3 — LIVE SHOPIFY DATA + PACK CHOICE
     LIVE = data from the collection (Shopify) or a saved snapshot (local test page).
     Variant format: [title, price, compareAtPrice|null, available 1|0, variantId].
     VSEL = which variant to sell: [ "Try first" text, "Value pack" text ] matched
     against the variant title. If no match, cheapest / biggest available is used.
     ============================================================================ */
  /* LIVE: names, variants [title, price, mrp, available, variantId] and photos from goofytails.com.
   Regenerated by scripts/fetch-quiz-live.py — do not edit by hand. */
  const LIVE = GTQ.products
    ? gtqLive(GTQ.products)
    : /*LIVE*/ {
        dBroth: {
          t: 'Goofy Tails Chicken Bone Broth for Dogs and Puppies',
          v: [
            ['100 ml / Pack of 3', 302.0, 318.0, 1, 46189278953622],
            ['100 ml / Pack of 5', 503.0, 530.0, 1, 41111623139478],
            ['100 ml / Pack of 15', 1431.0, 1590.0, 1, 46228042481814],
            ['100 ml / Pack of 30', 2862.0, 3180.0, 1, 46228042449046],
          ],
          img: ['img/quiz/dBroth.jpg', 'img/quiz/dBroth-2.jpg', 'img/quiz/dBroth-3.jpg'],
        },
        dLamb: {
          t: 'Goofy Tails Lamb Bone Broth for Dogs and Puppies',
          v: [
            ['100ml / Pack of 3', 313.0, 330.0, 1, 41852928983190],
            ['100ml / Pack of 5', 522.0, 550.0, 1, 41852928950422],
            ['100ml / Pack of 15', 1485.0, 1650.0, 1, 42562555445398],
            ['100ml / Pack of 30', 2970.0, 3300.0, 1, 41852929015958],
          ],
          img: ['img/quiz/dLamb.jpg', 'img/quiz/dLamb-2.jpg', 'img/quiz/dLamb-3.jpg'],
        },
        dKit: {
          t: 'Hydration Kit for Dogs | Chicken Bone Broth for Dogs | Canine Revive+ | Hemp Paw Balm',
          v: [
            ['Hydration Kit / Single Bag', 747.0, 747.0, 1, 46404880105622],
            ['Hydration Kit / 2 Bags', 1494.0, 1494.0, 1, 46404880138390],
            ['Hydration Kit / 3 Bags', 2241.0, 2241.0, 1, 46404880171158],
          ],
          img: ['img/quiz/dKit.jpg', 'img/quiz/dKit-2.jpg', 'img/quiz/dKit-3.jpg'],
        },
        dRevive: {
          t: 'Goofy Tails Canine Revive + Hydration, Nutrition booster, ORS for Dogs',
          v: [
            ['Pack of 1', 250.0, 200.0, 1, 45056257065110],
            ['Pack of 3', 712.0, 750.0, 1, 45056257097878],
            ['Pack of 5', 1125.0, 1250.0, 1, 45056257130646],
          ],
          img: ['img/quiz/dRevive.jpg', 'img/quiz/dRevive-2.jpg', 'img/quiz/dRevive-3.jpg'],
        },
        dVital: {
          t: 'Goofy Tails Canine Vitality + Wellness & Immunity Booster Supplement for Dogs',
          v: [
            ['Pack of 1', 180.0, null, 1, 45056154206358],
            ['Pack of 3', 540.0, 540.0, 1, 45056154239126],
            ['Pack of 5', 900.0, 900.0, 1, 45056154271894],
          ],
          img: ['img/quiz/dVital.jpg', 'img/quiz/dVital-2.jpg', 'img/quiz/dVital-3.jpg'],
        },
        dMob: {
          t: 'Goofy Tails Canine Mobility+ Supplement for Dogs | Joint Support | Mobility Booster',
          v: [
            ['Small - Medium Dogs / Single Box', 900.0, 900.0, 0, 46359754047638],
            ['Small - Medium Dogs / Box of 2', 1710.0, 1800.0, 0, 46360865177750],
            ['Small - Medium Dogs / Box of 3', 2430.0, 2700.0, 0, 46360865210518],
            ['Large - XL Dogs / Single Box', 1650.0, 1650.0, 1, 46359754080406],
            ['Large - XL Dogs / Box of 2', 2185.0, 2300.0, 1, 46360865243286],
            ['Large - XL Dogs / Box of 3', 4455.0, 4950.0, 1, 46360865276054],
          ],
          img: ['img/quiz/dMob.jpg', 'img/quiz/dMob-2.jpg', 'img/quiz/dMob-3.jpg'],
        },
        dTopper: { missing: 1 },
        dQuinoa: {
          t: 'Goofy Tails Chicken and Quinoa Wholesome Meals for Dogs and Puppies',
          v: [
            ['100g Pack / Single Meal Pack', 106.0, 106.0, 1, 44228985028758],
            ['100g Pack / 7 Meal Pack', 705.0, 742.0, 1, 44348430418070],
            ['100g Pack / 15 Meal Pack', 1431.0, 1590.0, 1, 46237094805654],
            ['200g Pack / Single Meal Pack', 200.0, 200.0, 1, 44348429271190],
            ['200g Pack / 7 Meal Pack', 1330.0, 1400.0, 1, 44348430483606],
            ['200g Pack / 15 Meal Pack', 2700.0, 3000.0, 1, 46237094838422],
            ['300g Pack / Single Meal Pack', 300.0, 300.0, 1, 46237094871190],
            ['300g Pack / 7 Meal Pack', 1995.0, 2100.0, 1, 46237094903958],
            ['300g Pack / 15 Meal Pack', 4050.0, 4500.0, 0, 46237094936726],
          ],
          img: ['img/quiz/dQuinoa.jpg', 'img/quiz/dQuinoa-2.jpg', 'img/quiz/dQuinoa-3.jpg'],
        },
        dHerbs: {
          t: 'Goofy Tails Chicken and Herbs Gluten & Grain Free Food for Dogs and Puppies',
          v: [
            ['100g / Single Meal Pack', 106.0, 106.0, 1, 45186426601622],
            ['100g / 7 Meal Pack', 705.0, 742.0, 1, 44244721893526],
            ['100g / 15 Meal Pack', 1431.0, 1590.0, 1, 44244721926294],
          ],
          img: ['img/quiz/dHerbs.jpg', 'img/quiz/dHerbs-2.jpg', 'img/quiz/dHerbs-3.jpg'],
        },
        dLambR: {
          t: 'Goofy Tails Lamb and Rosemary Gluten & Grain Free Food for Dogs and Puppies',
          v: [
            ['100g / Single Meal Pack', 106.0, 106.0, 1, 45186425487510],
            ['100g / 7 Meal Pack', 705.0, 742.0, 1, 44244852408470],
            ['100g / 15 Meal Pack', 1431.0, 1590.0, 1, 44244852441238],
          ],
          img: ['img/quiz/dLambR.jpg', 'img/quiz/dLambR-2.jpg', 'img/quiz/dLambR-3.jpg'],
        },
        dBuff: {
          t: 'Goofy Tails Buff & Berry Fresh Food for Dogs and Puppies',
          v: [
            ['200g / Single Meal Pack', 200.0, 200.0, 1, 46246060458134],
            ['200g / 7 Meal Pack', 1330.0, 1400.0, 1, 46246060490902],
            ['200g / 15 Meal Pack', 2700.0, 3000.0, 1, 46246060523670],
            ['300g / Single Meal Pack', 300.0, 300.0, 1, 46246060556438],
            ['300g / 7 Meal Pack', 1995.0, 2100.0, 1, 46246060589206],
            ['300g / 15 Meal Pack', 4050.0, 4500.0, 1, 46246060621974],
          ],
          img: ['img/quiz/dBuff.jpg', 'img/quiz/dBuff-2.jpg', 'img/quiz/dBuff-3.jpg'],
        },
        dTrial: {
          t: 'Wholesome Meals Trial Pack | 4 Flavours + 25ml Bone Broth',
          v: [['400g + 25ml', 400.0, 428.0, 1, 46219936530582]],
          img: ['img/quiz/dTrial.jpg', 'img/quiz/dTrial-2.jpg', 'img/quiz/dTrial-3.jpg'],
        },
        dDental: {
          t: 'Goofy Tails Active Dental Sticks | Dog Treats Non-Vegetarian | Dental Sticks for Dogs (Carrot & Yam)',
          v: [
            ['Pack of 1', 375.0, 375.0, 1, 42491469168790],
            ['Pack of 3', 1069.0, 1125.0, 1, 42491469201558],
            ['Pack of 5', 1687.0, 1875.0, 1, 42491504263318],
          ],
          img: ['img/quiz/dDental.jpg', 'img/quiz/dDental-2.jpg', 'img/quiz/dDental-3.jpg'],
        },
        dDentalV: {
          t: 'Oral Care Active Dental Vegetarian Dog Treat ( Seaweed)',
          v: [
            ['Pack of 1', 375.0, 375.0, 1, 42491356676246],
            ['Pack of 3', 1069.0, 1125.0, 1, 42491356741782],
            ['Pack of 5', 1687.0, 1875.0, 1, 42491568062614],
          ],
          img: ['img/quiz/dDentalV.jpg', 'img/quiz/dDentalV-2.jpg', 'img/quiz/dDentalV-3.jpg'],
        },
        dLiver: {
          t: 'Goofy Tails Freeze Dried Chicken Liver Cubes for Dogs-50g',
          v: [
            ['Pack 1 (50g x 1 =50g)', 395.0, 395.0, 1, 44274044174486],
            ['Pack 2 (50g x 2 =100g)', 790.0, 790.0, 1, 44274044207254],
            ['Pack 3 (50g x 3 =150g)', 1185.0, 1185.0, 1, 44274044240022],
          ],
          img: ['img/quiz/dLiver.jpg', 'img/quiz/dLiver-2.jpg', 'img/quiz/dLiver-3.jpg'],
        },
        dTwist: {
          t: 'Goofy Tails Active Chicken Twists - Dental Treats for Dogs',
          v: [
            ['Pack of 1', 280.0, 290.0, 0, 42559924502678],
            ['Pack of 2', 532.0, 580.0, 0, 42559924535446],
            ['Pack of 3', 746.0, 870.0, 1, 42559924568214],
          ],
          img: ['img/quiz/dTwist.jpg', 'img/quiz/dTwist-2.jpg', 'img/quiz/dTwist-3.jpg'],
        },
        dPaw: {
          t: 'Goofy Tails Paw Cream For Dogs and Puppies - 30g',
          v: [['Default Title', 285.0, 285.0, 1, 42551338008726]],
          img: ['img/quiz/dPaw.jpg', 'img/quiz/dPaw-2.jpg', 'img/quiz/dPaw-3.jpg'],
        },
        cBroth15: {
          t: 'Chicken Bone Broth for Cats and Kittens-25ml x 15 Pax (375ml)',
          v: [
            ['15 Packs (25ml X 15 Serves) / Single Box', 525.0, 525.0, 1, 46169561596054],
            ['15 Packs (25ml X 15 Serves) / Box of 2', 997.0, 1050.0, 1, 46379303436438],
            ['15 Packs (25ml X 15 Serves) / Box of 3', 1417.0, 1575.0, 1, 46379303469206],
          ],
          img: ['img/quiz/cBroth15.jpg', 'img/quiz/cBroth15-2.jpg', 'img/quiz/cBroth15-3.jpg'],
        },
        cBroth: {
          t: 'Chicken Bone Broth for Cats and Kittens',
          v: [
            ['100ml / Pack of 3', 302.0, 318.0, 1, 42297482117270],
            ['100ml / Pack of 5', 503.0, 530.0, 1, 42297482150038],
            ['100ml / Pack of 15', 1431.0, 1590.0, 1, 42297482182806],
            ['100ml / Pack of 30', 2862.0, 3180.0, 1, 42562488107158],
          ],
          img: ['img/quiz/cBroth.jpg', 'img/quiz/cBroth-2.jpg', 'img/quiz/cBroth-3.jpg'],
        },
        cSea: {
          t: 'Goofy Fresh Sea Food Bone Broth for Cats and Kittens',
          v: [
            ['100ml / Pack of 3', 302.0, 318.0, 1, 46237890805910],
            ['100ml / Pack of 5', 503.0, 530.0, 1, 42177484718230],
            ['100ml / Pack of 15', 1431.0, 1590.0, 1, 42704175661206],
            ['100ml / Pack of 30', 2862.0, 3180.0, 1, 42177484750998],
          ],
          img: ['img/quiz/cSea.jpg', 'img/quiz/cSea-2.jpg', 'img/quiz/cSea-3.jpg'],
        },
        cKit: {
          t: 'Hydration Kit for Cats | Chicken Bone Broth for Cats | Feline Revive+ | Hemp Paw Balm',
          v: [
            ['Hydration Kit / Single Bag', 530.0, 530.0, 1, 46401753186454],
            ['Hydration Kit / 2 Bags', 1060.0, 1060.0, 1, 46401756070038],
            ['Hydration Kit / 3 Bags', 1590.0, 1590.0, 1, 46401756102806],
          ],
          img: ['img/quiz/cKit.jpg', 'img/quiz/cKit-2.jpg', 'img/quiz/cKit-3.jpg'],
        },
        cRevive: {
          t: 'Feline Revive + Hydration, Nutrition booster, ORS for Cats',
          v: [
            ['Pack of 1', 250.0, 250.0, 1, 45123138584726],
            ['Pack of 3', 712.0, 750.0, 1, 45123138617494],
            ['Pack of 5', 1125.0, 1250.0, 1, 45123138650262],
          ],
          img: ['img/quiz/cRevive.jpg', 'img/quiz/cRevive-2.jpg', 'img/quiz/cRevive-3.jpg'],
        },
        cVital: {
          t: 'Feline Vitality + Wellness & Immunity Booster Supplement for Cats',
          v: [
            ['Pack of 1', 180.0, null, 1, 45123141533846],
            ['Pack of 3', 540.0, 540.0, 1, 45123141566614],
            ['Pack of 5', 900.0, 900.0, 1, 45123141599382],
          ],
          img: ['img/quiz/cVital.jpg', 'img/quiz/cVital-2.jpg', 'img/quiz/cVital-3.jpg'],
        },
        cTrial: {
          t: 'Goofy Tails Trial Packs Cat Meals – Himalayan Trout and Chicken & Mackerel',
          v: [
            ['Pack 1', 522.0, 522.0, 1, 45847102423190],
            ['Pack 2', 1044.0, 1044.0, 1, 45847102455958],
          ],
          img: ['img/quiz/cTrial.jpg', 'img/quiz/cTrial-2.jpg', 'img/quiz/cTrial-3.jpg'],
        },
        cTrout: {
          t: 'Goofy Tails Himalayan Trout Wet Cat and Kitten Food',
          v: [
            ['210g / Single Box (210g)', 261.0, 261.0, 1, 45674961141910],
            ['210g / 2 Boxes (420g)', 496.0, 522.0, 1, 46278697353366],
            ['210g / 3 Boxes (630g)', 705.0, 783.0, 1, 46278697386134],
          ],
          img: ['img/quiz/cTrout.jpg', 'img/quiz/cTrout-2.jpg', 'img/quiz/cTrout-3.jpg'],
        },
        cMackC: {
          t: 'Goofy Tails Mackerel and Chicken Wet Cat and Kitten Food',
          v: [
            ['210g / Single Box (210g)', 261.0, 261.0, 1, 45565577855126],
            ['210g / 2 Boxes (420g)', 496.0, 522.0, 1, 46287882190998],
            ['210g / 3 Boxes (630g)', 705.0, 783.0, 1, 46287882223766],
          ],
          img: ['img/quiz/cMackC.jpg', 'img/quiz/cMackC-2.jpg', 'img/quiz/cMackC-3.jpg'],
        },
        cFlax: {
          t: 'Goofy Tails Chicken and Flaxseed Wet Cat and Kitten Food',
          v: [
            ['1 Box (350g) = 5 Meals x 70g', 350.0, 350.0, 1, 42535900381334],
            ['2 Box (700g) = 10 Meals x 70g', 665.0, 700.0, 1, 42555521499286],
            ['3 Box (1050g) =  15 Meals x 70g', 945.0, 1050.0, 1, 42555521532054],
          ],
          img: ['img/quiz/cFlax.jpg', 'img/quiz/cFlax-2.jpg', 'img/quiz/cFlax-3.jpg'],
        },
        cTuna: {
          t: 'Goofy Tails Tuna and Anchovies Wet Cat Food and Kitten Food',
          v: [
            ['1 Pack x 350 g / 5 Meals', 350.0, 350.0, 1, 42535889895574],
            ['2 Pack x 350 g = 700 g/ 10 Meals', 665.0, 700.0, 1, 42555538047126],
            ['3 Pack x 350 g = 1050 g / 15 Meals', 945.0, 1050.0, 1, 43504110698646],
          ],
          img: ['img/quiz/cTuna.jpg', 'img/quiz/cTuna-2.jpg', 'img/quiz/cTuna-3.jpg'],
        },
        c15: {
          t: 'Goofy Tails 15 Meal Combo Wet Cat and Kitten Food',
          v: [
            ['15Meals (70g X 15 = 1.05kg)', 1050.0, 1050.0, 1, 42535901167766],
            ['30 Meals (70g X 30 = 2.1kg)', 2100.0, 2100.0, 1, 43122857410710],
          ],
          img: ['img/quiz/c15.jpg', 'img/quiz/c15-2.jpg', 'img/quiz/c15-3.jpg'],
        },
        cMackS: {
          t: 'Goofy Tails Mackerel and Seaweed Wet Cat and Kitten Food',
          v: [
            ['1 Pack x 350 g / 5 Meals', 350.0, 350.0, 1, 42535899267222],
            ['2 Pack x 350 g = 700 g/ 10 Meals', 665.0, 700.0, 1, 42555544273046],
            ['3 Pack x 350 g= 1050 g / 15 Meals', 945.0, 1050.0, 1, 42555544305814],
          ],
          img: ['img/quiz/cMackS.jpg', 'img/quiz/cMackS-2.jpg', 'img/quiz/cMackS-3.jpg'],
        },
        cLiver: {
          t: 'Goofy Tails Freeze Dried Chicken Liver Cat Treats',
          v: [
            ['Pack 1 (30g x 1 =30g)', 300.0, 300.0, 1, 44256443236502],
            ['Pack 2 (30g x 2 =60g)', 600.0, 600.0, 1, 44256443269270],
            ['Pack 3 (30g x 3 =90g)', 900.0, 900.0, 1, 44256443302038],
          ],
          img: ['img/quiz/cLiver.jpg', 'img/quiz/cLiver-2.jpg', 'img/quiz/cLiver-3.jpg'],
        },
        cShrimp: {
          t: 'Goofy Tails Freeze Dried Shrimp Treats for Cats and Kittens',
          v: [
            ['Pack 1 (30g x 1 =30g)', 650.0, 650.0, 1, 42626527428758],
            ['Pack 2 (30g x 2 =60g)', 1300.0, 1300.0, 1, 42626527461526],
            ['Pack 3 (30g x 3 =90g)', 1950.0, 1950.0, 1, 42626527494294],
          ],
          img: ['img/quiz/cShrimp.jpg', 'img/quiz/cShrimp-2.jpg', 'img/quiz/cShrimp-3.jpg'],
        },
        cTunaT: { missing: 1 },
        cPaw: {
          t: 'Goofy Tails Paw Cream For Cats and Kittens - 30g Balm',
          v: [['Default Title', 285.0, 285.0, 1, 42551399055510]],
          img: ['img/quiz/cPaw.jpg', 'img/quiz/cPaw-2.jpg', 'img/quiz/cPaw-3.jpg'],
        },
        omega: {
          t: 'Goofy Tails Plant Based Omega Oil for Dogs and Cats',
          v: [
            ['50ml', 300.0, 300.0, 1, 42459110670486],
            ['100ml', 600.0, 600.0, 1, 42459110703254],
          ],
          img: ['img/quiz/omega.jpg', 'img/quiz/omega-2.jpg', 'img/quiz/omega-3.jpg'],
        },
        vet: {
          t: 'Online Consultation with Vet',
          v: [['Default Title', 500.0, 1000.0, 1, 42746729496726]],
          img: ['img/quiz/vet.jpg'],
        },
      }; /*END LIVE*/
  /* Shopify: GTQ.products is keyed by handle (from the section's collection). Re-key by quiz id. */
  function gtqLive(byHandle) {
    const o = {};
    for (const [id, p] of Object.entries(P)) {
      const x = byHandle[p.h];
      if (!x) {
        o[id] = { missing: 1 };
        continue;
      }
      o[id] = { t: x.t, v: x.v, img: x.img };
      if (x.ing) p.ing = x.ing; // metafield custom.quiz_ingredients overrides
      if (x.allergens && x.allergens.length) {
        const MAP = {
          soya: 'soy',
          soybean: 'soy',
          grains: 'grain',
          wheat: 'gluten',
          seafood: 'fish',
          shellfish: 'fish',
          milk: 'dairy',
          eggs: 'egg',
          buffalo: 'buff',
        };
        const add = x.allergens
          .map((s) => String(s).trim().toLowerCase())
          .filter(Boolean)
          .map((s) => MAP[s] || s);
        p.contains = [...new Set([...(p.contains || []), ...add])];
      }
    }
    return o;
  }
  /* Which live variant to sell: [try-first pattern, monthly-supply pattern]. */
  const VSEL = {
    dBroth: ['Pack of 3', 'Pack of 15'],
    dLamb: ['Pack of 3', 'Pack of 15'],
    cBroth: ['Pack of 3', 'Pack of 15'],
    cSea: ['Pack of 3', 'Pack of 15'],
    dKit: ['Single', '2 Bags'],
    cKit: ['Single', '2 Bags'],
    dRevive: ['Pack of 1', 'Pack of 5'],
    dVital: ['Pack of 1', 'Pack of 5'],
    cRevive: ['Pack of 1', 'Pack of 5'],
    cVital: ['Pack of 1', 'Pack of 5'],
    dMob: ['Single Box', 'Box of 2'],
    cBroth15: ['Single Box', 'Box of 2'],
    cTrial: ['Pack 1', 'Pack 2'],
    cTrout: ['Single Box', '3 Boxes'],
    cMackC: ['Single Box', '3 Boxes'],
    cFlax: ['1 Box', '3 Box'],
    cTuna: ['1 Pack', '3 Pack'],
    cMackS: ['1 Pack', '3 Pack'],
    c15: ['15Meals', '30 Meals'],
    dDental: ['Pack of 1', 'Pack of 3'],
    dDentalV: ['Pack of 1', 'Pack of 3'],
    cLiver: ['Pack 1', 'Pack 2'],
    cShrimp: ['Pack 1', 'Pack 2'],
    dLiver: ['Pack 1', 'Pack 2'],
    dTwist: ['', ''],
    omega: ['50ml', '100ml'],
    dQuinoa: ['Single', '15 Meal'],
    dHerbs: ['Single', '15 Meal'],
    dLambR: ['Single', '15 Meal'],
    dBuff: ['Single', '15 Meal'],
  };
  function liveCands(id) {
    const L = LIVE[id];
    if (!L || L.missing) return null;
    let c = L.v;
    if (id === 'dMob') c = c.filter((x) => x[0].startsWith(A.kg > 25 ? 'Large' : 'Small'));
    if (P[id].sizes && A.species === 'dog' && A.kg) {
      const sz = mealPlan(id).size;
      const m = c.filter((x) => x[0].startsWith(sz + 'g'));
      if (m.length) c = m;
    }
    return c;
  }
  function liveAvail(id) {
    const c = liveCands(id);
    if (c === null) return !LIVE[id] || Object.keys(LIVE).length === 0 ? true : false;
    return c.some((x) => x[3]);
  }
  function liveTitle(id) {
    const L = LIVE[id];
    if (!L || !L.t) return P[id].name;
    return L.t
      .replace(/^Goofy (Tails )?(Fresh )?/, '')
      .split(' | ')[0]
      .replace(/\s+-\s*\d+\s*g.*$/, '')
      .trim();
  }
  function liveImgs(id) {
    const L = LIVE[id];
    return L && L.img && L.img.length ? L.img : ['img/quiz/' + id + '.jpg'];
  }
  const FMT_LABEL = {
    broth: 'Bone broth',
    supplement: 'Supplement',
    meal: 'Meal',
    treat: 'Treat',
    care: 'Care',
    kit: 'Kit',
  };

  /* ============================================================================
     SECTION 4 — FEEDING AMOUNTS (DOSE, RULES)
     bands = [[maxKg, amount], ...] — first row whose maxKg >= pet weight is used.
     Source of every number is in its src field. Change only with product-team sign-off.
     ============================================================================ */
  /* Doses: copied from each PDP's "How do I serve…" FAQ. "Up to" per day. */
  const DOSE = {
    dogStd: {
      bands: [
        [10, 50],
        [20, 100],
        [40, 100],
        [999, 150],
      ],
      src: 'Canine Revive+/Vitality+ FAQ',
    },
    catRevive: {
      bands: [
        [5, 30],
        [10, 60],
        [999, 80],
      ],
      src: 'Feline Revive+ FAQ',
    },
    catVital: {
      bands: [
        [5, 30],
        [10, 60],
        [999, 60],
      ],
      src: 'Feline Vitality+ FAQ',
    },
  };

  /* Verified amounts copied from data/feeding-rules.json (pack artwork / product descriptions / Kartik 13 Oct 2026). */
  const RULES = {
    catBroth: {
      bands: [
        [5, 30],
        [10, 60],
        [999, 80],
      ],
      kitten: 15,
      src: 'Cat broth pack chart, "How much should your cat eat?"',
    },
    dogTopper: {
      bands: [
        [5, 1],
        [10, 2],
        [20, 3],
        [30, 4],
        [999, 4.5],
      ],
      src: 'Super Food Topper product description',
    },
    dental: {
      bands: [
        [10, 1],
        [20, 2],
        [40, 3],
        [999, 4],
      ],
      src: 'Active Dental Sticks product description',
    },
    catCubes: {
      bands: [
        [3, 7],
        [5, 10],
        [999, 13],
      ],
      src: 'Freeze-dried liver cat treats product description',
    },
    dogCubes: {
      bands: [
        [10, 5],
        [20, 10],
        [999, 15],
      ],
      src: 'Freeze-dried liver cubes product description',
    },
    catTrout: {
      adult: [
        [2, 140, 140],
        [999, 210, 280],
      ],
      kitten: [
        [1, 140, 140],
        [999, 210, 280],
      ],
      src: 'Trout / Mackerel & Chicken feeding guide (70 g pouches)',
    },
    catWhole: {
      adult: [
        [2, 70, 70],
        [4, 70, 140],
        [999, 140, 280],
      ],
      src: 'Flaxseed, Tuna & Anchovies and combo feeding guide (70 g meals)',
    },
    dogMeal: {
      perKg: { low: [30, 30], moderate: [35, 40], high: [40, 50], puppy: [40, 40] },
      topper: 0.5,
      src: 'Goofy Tails meal guide: 3–5% of bodyweight (13 Oct 2026)',
    },
  };

  /* ============================================================================
     SECTION 5 — GOALS -> PRODUCTS (CONCERNS, EXTRAS)
     CONCERNS[goal][species] = [[productId, reason shown to customer], ...]
     Order matters: first = strongest match. Goal keys: food fussy hydration tummy
     joints coat dental recovery. EXTRAS = "Also good for" suggestions.
     ============================================================================ */
  /* Concern routing: [product, reason]. Reasons paraphrase the product's own FAQ. */
  const CONCERNS = {
    fussy: {
      label: 'Fussy eater',
      sub: 'Leaves food, poor appetite',
      short: 'fussy eating',
      dog: [
        [
          'dVital',
          'The Vitality+ FAQ names fussy eaters and dogs with poor appetite first in its "who is it for" list.',
        ],
        [
          'dBroth',
          'Broth adds smell and moisture, and the FAQ calls it particularly useful as a topper for dry kibble.',
        ],
        ['dTopper', 'A meat topper turns the same bowl into a new meal without a full diet switch.'],
        ['dLamb', 'A second broth flavour for dogs who get bored of one.'],
        [
          'dLiver',
          '100% chicken liver, freeze-dried. A small, high-value reward that tempts a fussy dog back to the bowl (FAQ).',
        ],
      ],
      cat: [
        [
          'cBroth15',
          'Our best-selling cat broth. The FAQ calls it a topper that makes meals more appealing while adding moisture.',
        ],
        ['cVital', 'The Vitality+ FAQ lists fussy or picky eaters among the cats it is made for.'],
        [
          'cTunaT',
          '100% tuna, freeze-dried to keep 99% of its nutrition. A strong smell that tempts a cat back to the bowl.',
        ],
        ['cSea', 'A seafood broth for cats who turn down chicken, or need to avoid it.'],
        ['cTuna', 'Tuna and anchovies: a strong fish flavour picky cats go for, and no chicken.'],
      ],
    },
    hydration: {
      label: 'Not drinking enough water',
      sub: 'Also for hot weather',
      short: 'hydration',
      dog: [
        ['dKit', 'Broth plus Canine Revive+ electrolytes in one kit, for hot weather and after exercise.'],
        ['dBroth', 'Bone broth gets water in with food instead of from the bowl.'],
        ['dRevive', 'The FAQ recommends Revive+ during heat stress, after exercise and for dehydration.'],
        ['dLamb', "A lamb bone broth, for dogs who can't have chicken."],
      ],
      cat: [
        [
          'cKit',
          'Broth plus Feline Revive+ in one kit. Cats get most of their water from food, so this works with how they are built.',
        ],
        ['cBroth15', 'A 25 ml shot over food adds water a cat will actually take.'],
        [
          'cTrial',
          'Wet food carries far more water than kibble. The trial pack lets you find the flavour your cat accepts.',
        ],
        [
          'cTuna',
          "Tuna, anchovies and water, with no chicken. Wet food adds the water many cats don't drink.",
        ],
      ],
    },
    tummy: {
      label: 'Tummy troubles',
      sub: 'Loose stools, gas, sensitive gut',
      short: 'digestion',
      dog: [
        [
          'dVital',
          'Contains fenugreek fibre, a prebiotic. The FAQ lists dogs with weak digestion or frequent tummy issues.',
        ],
        ['dBroth', 'Gentle and high in moisture, with no added fat or sodium (FAQ).'],
        ['dQuinoa', 'A gentle everyday fresh meal for switching away from kibble slowly.'],
        ['dLamb', 'Lamb bone broth: the same gentle moisture, without chicken.'],
        ['dBuff', 'Made on buffalo and buff bone broth. Buffalo is a naturally hypoallergenic protein.'],
      ],
      cat: [
        ['cVital', 'Prebiotic fenugreek fibre. The FAQ lists cats with digestive issues or a sensitive gut.'],
        ['cBroth', 'Gentle moisture with no added fat or sodium (FAQ).'],
        ['cFlax', 'A single-protein chicken recipe with flaxseed fibre.'],
        ['cTuna', 'A short list: tuna, anchovies, water, flaxseed. No chicken.'],
      ],
    },
    joints: {
      label: 'Joints and mobility',
      sub: 'Stiffness, slowing down, joint-prone breed',
      short: 'joints',
      dog: [
        [
          'dMob',
          'Glucosamine, chondroitin, collagen types I, II and III, green-lipped mussel, MSM and hyaluronic acid (FAQ). Made for senior dogs and joint-prone breeds.',
        ],
        ['dBroth', '24-hour simmered bones give naturally occurring collagen.'],
        ['omega', 'Plant omegas. The FAQ lists support for joint health and mobility.'],
      ],
      cat: [
        [
          'cVital',
          "There's no Mobility+ for cats yet. Vitality+ is the closest match: collagen peptides and boswellia for joints.",
        ],
        ['cBroth15', 'Naturally occurring collagen from the long bone simmer.'],
        ['omega', 'Plant omegas. The FAQ lists support for joint health and mobility.'],
      ],
    },
    coat: {
      label: 'Skin, coat or paws',
      sub: 'Dull coat, itching, shedding, dry or cracked pads',
      short: 'skin and coat',
      dog: [
        [
          'omega',
          'Hemp seed oil with essential fatty acids. The FAQ lists a healthy coat and skin first. Contains no CBD.',
        ],
        ['dVital', 'Collagen peptides. The FAQ lists skin and coat health.'],
        ['dPaw', 'A hemp butter balm for dry or cracked pads, applied directly.'],
        ['dBroth', 'Collagen and amino acids from the bones.'],
      ],
      cat: [
        [
          'omega',
          'Hemp seed oil with essential fatty acids. The FAQ lists a healthy coat and skin first. Contains no CBD.',
        ],
        ['cVital', 'The FAQ says it promotes a shiny coat and healthy skin.'],
        ['cPaw', 'A hemp butter balm for dry pads.'],
        ['cShrimp', '100% shrimp. It brings selenium and B12, which the FAQ links to coat and skin.'],
      ],
    },
    recovery: {
      label: 'Recovering or low energy',
      sub: 'After illness, surgery or a tired spell',
      short: 'recovery',
      dog: [
        [
          'dRevive',
          'Electrolytes, amino acids and prebiotics. The FAQ says it is for recovery from illness, vomiting, diarrhoea or after a vet visit.',
        ],
        [
          'dVital',
          'For a tired spell: ashwagandha and turmeric. The FAQ lists dogs that seem weak or low on energy.',
        ],
        ['dBroth', 'A gentle way to get fluids and protein in when appetite is low.'],
      ],
      cat: [
        [
          'cRevive',
          'The FAQ says it is for recovery from illness, digestive upset, diarrhoea or after a vet visit.',
        ],
        [
          'cVital',
          'For a tired spell: ashwagandha and turmeric. The FAQ lists cats that appear weak, tired or stressed.',
        ],
        ['cBroth15', 'Small 25 ml shots are easy to offer a cat who is barely eating.'],
      ],
    },
    dental: {
      label: 'Teeth and breath',
      sub: 'Bad breath, plaque, tartar',
      short: 'teeth',
      dog: [
        ['dDental', 'Dental sticks clean teeth mechanically as the dog chews.'],
        ['dTwist', 'Chicken twists made as a dental chew.'],
        ['dDentalV', "A vegetarian seaweed stick, for dogs who don't eat meat treats."],
      ],
      cat: [
        ['cTunaT', 'Crunchy freeze-dried tuna helps scrape away plaque and tartar as your cat chews.'],
        ['cShrimp', 'A crunchy freeze-dried treat that gives your cat something to chew.'],
      ],
    },
    food: {
      label: 'Better everyday food',
      sub: 'Moving to fresh, human-grade meals',
      short: 'better food',
      dog: [
        [
          'dTrial',
          'Four meal flavours plus a broth shot, so your dog picks the favourite before you commit.',
        ],
        ['dQuinoa', 'Chef-made with human-grade chicken, quinoa and vegetables.'],
        ['dHerbs', 'Grain- and gluten-free, high in protein.'],
        ['dLambR', 'Grain-free, made with lamb organ meats and lamb bone broth. No chicken.'],
        [
          'dBuff',
          'Buffalo with buff bone broth. A naturally hypoallergenic protein, and the FAQ suggests it for large, giant and working breeds.',
        ],
      ],
      cat: [
        ['cTrial', 'Trout and chicken-mackerel recipes in one box. Find the flavour first.'],
        ['c15', '15 meals across recipes for daily wet feeding.'],
        [
          'cTrout',
          'Trout and anchovies with taurine. The FAQ lists it for picky eaters and urinary wellness.',
        ],
        ['cMackC', '40–45% chicken with mackerel (FAQ).'],
        ['cTuna', 'Tuna and anchovies, no chicken. For cats who need to avoid chicken.'],
        ['cMackS', 'Mackerel with seaweed, no chicken.'],
        ['cFlax', 'Chicken, flaxseed and seaweed extract, with no fish. For cats who need to avoid fish.'],
      ],
    },
  };

  /* Everyday extras: [product, reason, show-if, priority]. Never shown if excluded or already in the plan. */
  const EXTRAS = {
    dog: [
      [
        'dBroth',
        'A daily broth topper keeps water intake up and adds collagen, whatever else is in the bowl.',
        null,
        (a) => (a.diet === 'kibble' ? 3 : 1),
      ],
      [
        'dDental',
        'Daily dental sticks cut down plaque between brushings.',
        (a) => a.ageMonths >= 4,
        (a) => (a.ageMonths >= 36 ? 2 : 1),
      ],
      [
        'dMob',
        'Joint support before stiffness shows, for seniors and joint-prone breeds.',
        (a) => a.ageMonths >= 84 || JOINT_BREEDS.includes(a.breed),
        (a) => 4,
      ],
      ['omega', 'Plant omegas for skin, coat and joints.', (a) => a.ageMonths >= 12, (a) => 1],
      ['dVital', 'Everyday wellness: prebiotics, collagen and ashwagandha.', null, (a) => 1],
      [
        'dLiver',
        'A high-value training reward: 100% chicken liver, freeze-dried.',
        (a) => a.ageMonths >= 3,
        (a) => (a.ageMonths < 12 ? 3 : 1),
      ],
    ],
    cat: [
      [
        'cTunaT',
        'Crunchy freeze-dried tuna helps remove plaque and tartar as your cat chews, and makes a good reward.',
        (a) => a.ageMonths >= 3,
        (a) => 3,
      ],
      [
        'cBroth15',
        "Most cats don't drink enough. A 25 ml shot over food adds water they will actually take.",
        (a) => a.ageMonths >= 4,
        (a) => (a.diet === 'kibble' ? 4 : 2),
      ],
      [
        'cVital',
        'Everyday wellness: prebiotics, collagen and ashwagandha.',
        null,
        (a) => (a.ageMonths >= 84 ? 3 : 1),
      ],
      ['omega', 'Plant omegas for skin, coat and joints.', (a) => a.ageMonths >= 12, (a) => 1],
      [
        'cLiver',
        'A reward cats go for: 100% chicken liver, freeze-dried.',
        (a) => a.ageMonths >= 3,
        (a) => 1,
      ],
    ],
  };

  /* ============================================================================
     SECTION 6 — BREEDS / WEIGHT BANDS
     BREEDS row: [name, minKg, maxKg, search aliases, 1 = show in top list].
     WBANDS = weight buttons. JOINT_BREEDS get extra joint support.
     ============================================================================ */
  const BREEDS = {
    dog: [
      ['Indie / Indian Pariah', 15, 25, 'desi street mongrel pariah indian dog', 1],
      ['Labrador Retriever', 25, 36, 'lab labrador', 1],
      ['Golden Retriever', 25, 34, 'golden', 1],
      ['German Shepherd', 22, 40, 'gsd alsatian', 1],
      ['Beagle', 9, 14, '', 1],
      ['Pug', 6, 9, '', 1],
      ['Shih Tzu', 4, 7, 'shitzu shihtzu', 1],
      ['Pomeranian', 2, 4, 'pom', 1],
      ['Indian Spitz', 5, 12, 'spitz', 1],
      ['Lhasa Apso', 5, 8, 'lhasa', 1],
      ['Maltese', 2, 4, '', 1],
      ['Dachshund', 7, 15, 'sausage dog doxie', 1],
      ['Cocker Spaniel', 11, 15, 'spaniel', 1],
      ['Rottweiler', 35, 60, 'rottie rotti', 1],
      ['Doberman', 27, 45, 'dobermann doberman pinscher', 1],
      ['Boxer', 25, 32, '', 1],
      ['Siberian Husky', 16, 27, 'husky', 1],
      ['Great Dane', 45, 80, '', 1],
      ['Saint Bernard', 54, 82, 'st bernard st. bernard', 1],
      ['Belgian Malinois', 20, 30, 'malinois', 1],
      ['Greyhound', 27, 40, '', 1],
      ['Cane Corso', 40, 50, 'italian mastiff', 1],
      ['English Mastiff', 54, 100, 'mastiff', 1],
      ['French Mastiff', 45, 65, 'dogue de bordeaux', 1],
      ['Tibetan Mastiff', 45, 73, '', 1],
      ['Himalayan Sheepdog', 25, 40, 'bhotia', 1],
      ['Pitbull', 14, 27, 'pit bull american pit bull terrier apbt', 1],
      ['Chihuahua', 1.5, 3, '', 1],
      ['Yorkshire Terrier', 2, 3.5, 'yorkie', 1],
      ['French Bulldog', 8, 13, 'frenchie', 1],
      ['English Bulldog', 18, 25, 'bulldog', 1],
      ['Poodle (Standard)', 20, 32, 'poodle', 1],
      ['Toy Poodle', 2, 4, 'poodle', 0],
      ['Miniature Poodle', 5, 8, 'poodle', 0],
      ['Akita', 32, 59, '', 1],
      ['Chow Chow', 20, 32, 'chow', 1],
      ['Jack Russell Terrier', 6, 8, 'jack russell', 1],
      ['Corgi', 10, 14, 'welsh corgi pembroke', 1],
      ['Basset Hound', 20, 29, 'basset', 1],
      ['Bull Terrier', 22, 32, '', 1],
      ['Rajapalayam', 22, 30, '', 0],
      ['Mudhol Hound', 20, 28, 'caravan hound mudhol', 0],
      ['Chippiparai', 15, 20, '', 0],
      ['Kombai', 20, 30, '', 0],
      ['Kanni', 16, 22, '', 0],
      ['Rampur Greyhound', 27, 32, '', 0],
      ['Gaddi Kutta', 35, 55, 'gaddi', 0],
      ['Bakharwal', 30, 60, 'bakarwal', 0],
      ['Jonangi', 13, 18, '', 0],
      ['Pandikona', 15, 25, '', 0],
      ['Bullmastiff', 45, 60, 'bull mastiff', 0],
      ['Dalmatian', 20, 32, '', 0],
      ['Border Collie', 14, 20, 'collie', 0],
      ['Shiba Inu', 8, 11, 'shiba', 0],
      ['Samoyed', 16, 30, '', 0],
      ['Alaskan Malamute', 34, 39, 'malamute', 0],
      ['Cavalier King Charles Spaniel', 5, 8, 'cavalier', 0],
      ['Bichon Frise', 5, 8, 'bichon', 0],
      ['Miniature Schnauzer', 5, 9, 'schnauzer', 0],
      ['Weimaraner', 25, 40, '', 0],
      ['Labradoodle', 15, 30, 'doodle', 0],
      ['Goldendoodle', 15, 35, 'doodle', 0],
      ['Australian Shepherd', 16, 32, 'aussie', 0],
      ['Bernese Mountain Dog', 32, 50, 'bernese', 0],
      ['Newfoundland', 45, 70, 'newfie', 0],
      ['Rhodesian Ridgeback', 29, 41, 'ridgeback', 0],
      ['Dogo Argentino', 35, 45, 'dogo', 0],
      ['American Bully', 20, 50, 'bully', 0],
      ['Afghan Hound', 23, 27, 'afghan', 0],
      ['Shar Pei', 18, 29, 'sharpei', 0],
      ['Whippet', 10, 14, '', 0],
      ['Indie × Labrador mix', 18, 30, 'lab mix indie lab cross labrador cross', 0],
      ['Indie × German Shepherd mix', 20, 32, 'gsd mix alsatian mix indie gsd cross', 0],
      ['Indie × Spitz mix', 6, 14, 'spitz mix indie spitz cross', 0],
      ['Indie × Beagle mix', 9, 16, 'beagle mix indie beagle cross', 0],
      ['Labrador × German Shepherd (Sheprador)', 25, 40, 'sheprador lab gsd mix', 0],
      ['Goldador', 25, 36, 'golden labrador golden lab mix', 0],
      ['Labsky (Labrador × Husky)', 18, 36, 'huskador lab husky mix', 0],
      ['Goberian (Golden × Husky)', 20, 36, 'golden husky mix', 0],
      ['Boxador (Boxer × Labrador)', 25, 40, 'boxer lab mix', 0],
      ['Cockapoo', 5, 11, 'cocker poodle mix doodle', 0],
      ['Maltipoo', 2, 9, 'maltese poodle mix', 0],
      ['Cavapoo', 5, 9, 'cavalier poodle mix', 0],
      ['Yorkiepoo', 2, 6, 'yorkie poodle mix', 0],
      ['Shih-Poo', 4, 8, 'shihpoo shih tzu poodle mix', 0],
      ['Peekapoo', 2, 9, 'pekingese poodle mix', 0],
      ['Schnoodle', 5, 9, 'schnauzer poodle mix', 0],
      ['Aussiedoodle', 14, 32, 'australian shepherd poodle mix doodle', 0],
      ['Bernedoodle', 15, 40, 'bernese poodle mix doodle', 0],
      ['Sheepadoodle', 20, 40, 'sheepdog poodle mix doodle', 0],
      ['Newfypoo', 30, 60, 'newfoundland poodle mix doodle', 0],
      ['Pomsky', 7, 14, 'pomeranian husky mix', 0],
      ['Puggle', 7, 14, 'pug beagle mix', 0],
      ['Morkie', 2, 6, 'maltese yorkie mix', 0],
      ['Chiweenie', 2, 6, 'chihuahua dachshund mix', 0],
      ['Pomchi', 2, 5, 'pomeranian chihuahua mix', 0],
      ['Shorkie', 3, 7, 'shih tzu yorkie mix', 0],
      ['Cavachon', 5, 9, 'cavalier bichon mix', 0],
      ['Beaglier', 5, 11, 'beagle cavalier mix', 0],
      ['Sprocker Spaniel', 9, 18, 'springer cocker mix', 0],
      ['Golden Collie', 23, 34, 'golden retriever collie mix', 0],
      ['Indie × Rottweiler mix', 20, 35, 'rottweiler mix rottie mix indie rott cross', 0],
      ['Indie × Doberman mix', 20, 32, 'doberman mix indie dobe cross', 0],
      ['Indie × Husky mix', 15, 25, 'husky mix indie husky cross', 0],
      ['Pitsky (Pitbull × Husky)', 14, 32, 'pitbull husky mix', 0],
      ['Jack Chi', 4, 8, 'jack russell chihuahua mix', 0],
      ['Chi-Poo', 3, 5, 'chipoo chihuahua poodle mix', 0],
      ['Corgipoo', 7, 14, 'corgi poodle mix', 0],
      ['Doxiepoo', 5, 14, 'dachshund poodle mix doxie', 0],
      ['Frug (Pug × French Bulldog)', 7, 14, 'frenchie pug mix', 0],
      ['Mixed breed: small (under 10 kg)', 4, 9, 'mixed mix cross crossbreed not sure', 0],
      ['Mixed breed: medium (10 to 25 kg)', 10, 24, 'mixed mix cross crossbreed not sure', 0],
      ['Mixed breed: large (over 25 kg)', 26, 40, 'mixed mix cross crossbreed not sure', 0],
    ],
    cat: [
      ['Indie / Indian domestic', 3, 5, 'indian billi desi cat street cat moggy', 1],
      ['Persian', 3, 6, '', 1],
      ['Himalayan', 3, 6, '', 1],
      ['Siamese', 3, 5, '', 1],
      ['Bengal', 4, 7, '', 1],
      ['Maine Coon', 5, 11, '', 1],
      ['British Shorthair', 4, 8, 'bsh', 1],
      ['Ragdoll', 4, 9, '', 1],
      ['Bombay', 3, 5, 'black cat', 1],
      ['Siberian', 4, 8, '', 1],
      ['Norwegian Forest Cat', 4, 9, 'wegie', 1],
      ['Sphynx', 3, 5, 'hairless', 1],
      ['Scottish Fold', 3, 6, '', 1],
      ['Russian Blue', 3, 5, '', 1],
      ['Exotic Shorthair', 3, 6, 'exotic', 0],
      ['American Shorthair', 4, 7, '', 0],
      ['Abyssinian', 3, 5, 'aby', 0],
      ['Birman', 3, 6, '', 0],
      ['Burmese', 3, 6, '', 0],
      ['Turkish Angora', 2.5, 5, 'angora', 0],
      ['Turkish Van', 3, 8, '', 0],
      ['Oriental Shorthair', 3, 5, 'oriental', 0],
      ['Devon Rex', 2.5, 4, 'rex', 0],
      ['Munchkin', 2, 4, '', 0],
      ['Savannah', 5, 10, '', 0],
      ['Persian mix', 3, 6, 'persian cross indie persian', 0],
      ['Siamese mix', 3, 5, 'siamese cross', 0],
      ['Himalayan mix', 3, 6, 'himalayan cross', 0],
      ['Bengal mix', 4, 6, 'bengal cross', 0],
      ['Maine Coon mix', 4, 9, 'maine coon cross', 0],
      ['Ragamuffin', 4, 9, '', 0],
      ['Toyger', 3, 7, '', 0],
      ['Mixed / not sure', 3, 6, 'mixed mix not sure', 0],
    ],
  };
  const WBANDS = {
    dog: [
      ['xs', 'Under 10 kg', 6],
      ['m', '10–20 kg', 15],
      ['l', '20–40 kg', 30],
      ['xl', 'Over 40 kg', 45],
    ],
    cat: [
      ['xs', 'Under 5 kg', 4],
      ['m', '5–10 kg', 7],
      ['xl', 'Over 10 kg', 11],
    ],
  };
  const JOINT_BREEDS = [
    'Labrador Retriever',
    'Golden Retriever',
    'German Shepherd',
    'Rottweiler',
    'Great Dane',
    'Saint Bernard',
    'Dachshund',
    'Boxer',
    'Doberman',
    'Cane Corso',
    'English Mastiff',
    'French Mastiff',
    'Tibetan Mastiff',
    'Bullmastiff',
    'Belgian Malinois',
    'Akita',
    'Alaskan Malamute',
    'Newfoundland',
    'Bernese Mountain Dog',
    'Basset Hound',
    'Corgi',
    'Labradoodle',
    'Goldendoodle',
    'Gaddi Kutta',
    'Bakharwal',
    'Himalayan Sheepdog',
  ];

  /* ============================================================================
     SECTION 7 — ALLERGY / HEALTH QUESTIONS (FLAGS) + FAQ
     FLAGS row: [key, label, group allergy|med|vet]. Keys are used in recommend().
     ============================================================================ */
  const FLAGS = [
    ['chicken', 'Chicken allergy', 'allergy'],
    ['fish', 'Fish or seafood allergy', 'allergy'],
    ['egg', 'Egg allergy', 'allergy'],
    ['dairy', 'Dairy sensitivity', 'allergy'],
    ['gluten', 'Gluten / wheat allergy', 'allergy'],
    ['grain', 'Grain allergy', 'allergy'],
    ['soy', 'Soya allergy', 'allergy'],
    ['kidney', 'Kidney disease', 'med'],
    ['diabetes', 'Diabetes', 'med'],
    ['pancreas', 'Pancreatitis / low-fat diet', 'med'],
    ['meds', 'Under vet treatment or on medication', 'vet'],
    ['preg', 'Pregnant or nursing', 'vet'],
    ['none', 'None of these', 'none'],
  ];

  /* FAQ answers to surface on the results page (from live PDPs). */
  const FAQ = {
    transition_dog: [
      'How do I switch my dog to fresh meals?',
      'Over five days: 20% new food on day 1, then 40%, 60%, 80% and 100% on day 5. If you see any discomfort, check with your vet.',
      'Wholesome Meals FAQ',
    ],
    transition_cat: [
      'How do I introduce a new recipe to my cat?',
      'Over about 7 days, mixing small amounts of the new food into the usual food. Cats notice the smallest change in smell, so it can take a few tries.',
      'Cat Meals FAQ',
    ],
    storeSupp: [
      'How do I store a supplement pack once it is open?',
      'Keep it in a cool, dry place until opened. After opening, refrigerate straight away and use within 72 hours.',
      'Revive+, Vitality+ and Mobility+ FAQs',
    ],
    storeMeal: [
      'How long does an opened meal keep?',
      'Unopened packs keep at room temperature. Once opened, refrigerate and use within 24 hours.',
      'Meals FAQ',
    ],
    brothSoup: [
      'Is bone broth just soup?',
      'No. Soup is boiled for 1–2 hours. Our broth simmers bones for about 24 hours with apple cider vinegar to draw out collagen and minerals.',
      'Bone Broth FAQ',
    ],
    noSalt: [
      'Is there added salt or fat?',
      'No added sodium or fat. Visible fat is trimmed, and only the sodium naturally present in the ingredients remains.',
      'Broth & Meals FAQ',
    ],
    cbd: [
      'Does the omega oil contain CBD?',
      'No. It is pressed from hemp seeds and contains no CBD.',
      'Omega Oil FAQ',
    ],
    swollen: [
      'What if a pack arrives swollen?',
      'Take a photo and send it to care@goofytails.com for a fresh replacement.',
      'Broth FAQ',
    ],
    runny: [
      'What if there is runny poop or vomit after switching?',
      'Watch closely and keep them hydrated. If it lasts beyond 24 hours or gets worse, see a vet.',
      'Meals FAQ',
    ],
  };

  /* =====================================================================
   ENGINE — deterministic. No LLM decides a dose or an exclusion.
   ===================================================================== */
  /* ============================================================================
     SECTION 8 — RULES ENGINE  (recommend)
     recommend(answers) returns {picks, reasons, excluded, askFirst, notes, extras, soldOut}.
     Order of checks per product: missing > sold out > allergens > unverified list >
     kidney > diabetes > pancreatitis > age. Then scoring by goals, age, size, diet,
     and finally up to 3 picks (one per format family). Safety-critical — test after edits.
     ============================================================================ */
  function bandFor(kg, bands) {
    for (const [max, ml] of bands) {
      if (kg <= max) return ml;
    }
    return bands[bands.length - 1][1];
  }

  const ALLERGEN_WORDS = {
    chicken: /chicken/i,
    fish: /fish|tuna|trout|mackerel|anchov|shrimp|prawn|shellfish|mussel|salmon|sardine|seafood|\bEPA\b|\bDHA\b/i,
    egg: /\begg/i,
    dairy: /milk|whey|casein|lactose|cheese|curd|yog/i,
    lamb: /lamb/i,
    buff: /buff/i,
    gluten: /wheat|gluten|barley|\brye\b|\boat|brewer.?s yeast|semolina|maida|atta\b/i,
    grain: /\brice\b|wheat|corn|maize|\boat|barley|quinoa|millet|sorghum|\brye\b|maltodextrin|dextrose/i,
    soy: /\bsoy|soya|soybean|tofu|edamame/i,
  };
  function allergensOf(p) {
    const set = new Set(p.contains || []);
    const txt = p.ing || '';
    for (const [k, re] of Object.entries(ALLERGEN_WORDS)) if (re.test(txt)) set.add(k);
    return [...set];
  }
  function recommend(a) {
    const sp = a.species,
      flags = a.flags.filter((f) => f !== 'none');
    const allergies = flags.filter((f) =>
      ['chicken', 'fish', 'egg', 'dairy', 'gluten', 'grain', 'soy'].includes(f),
    );
    const ageM = a.ageMonths,
      kg = a.kg,
      name = a.name || (sp === 'dog' ? 'your dog' : 'your cat');
    const excluded = {},
      askFirst = {},
      soldOut = new Set();
    const pool = Object.entries(P).filter(([id, p]) => (p.sp === sp || p.sp === 'both') && id !== 'vet');

    for (const [id, p] of pool) {
      if (LIVE[id] && LIVE[id].missing) {
        excluded[id] = 'No longer on goofytails.com';
        continue;
      }
      if (!liveAvail(id)) soldOut.add(id);
      // Age limits first (17 Oct): a product that is too early for the pet's age is simply out,
      // so it never shows under "Ask us first" for another reason (e.g. kidney).
      if (ageM < 3 && (p.meal || p.fmt === 'supplement' || p.fmt === 'kit')) {
        excluded[id] = 'Under 3 months. Solid food and supplements come later';
        continue;
      }
      if (p.minMonths && ageM < p.minMonths) {
        excluded[id] = 'Not for ' + (sp === 'dog' ? 'puppies' : 'kittens') + " without a vet's OK";
        continue;
      }
      const hit = allergensOf(p).filter((x) => allergies.includes(x));
      if (!hit.length && p.vague && p.vague.some((x) => allergies.includes(x))) {
        askFirst[id] = 'Fibre and protein sources not stated on the pack';
        continue;
      }
      if (hit.length) {
        excluded[id] =
          'Contains ' +
          hit.join(' and ') +
          (p.ing
            ? ' (' + p.ing.split(/[,(.]/)[0].trim().toLowerCase() + (/[,.]/.test(p.ing) ? '…' : '') + ')'
            : '');
        continue;
      }
      if (allergies.length && p.unverified) {
        askFirst[id] = 'Full ingredient list not published yet';
        continue;
      }
      if (flags.includes('kidney') && p.broth) {
        excluded[id] = 'Bone broth base. Bone is high in phosphorus';
        continue;
      }
      if (flags.includes('kidney') && p.phos) {
        askFirst[id] =
          sp === 'dog'
            ? 'Cooked in bone broth. Check with your vet'
            : 'Phosphorus 0.4–0.7%. Check with your vet';
        continue;
      }
      if (flags.includes('diabetes') && p.sugar) {
        excluded[id] = 'Contains dextrose and maltodextrin';
        continue;
      }
      if (flags.includes('pancreas') && p.fatty) {
        excluded[id] = 'Contains roasted chicken skin';
        continue;
      }
    }
    const ok = (id) =>
      P[id] &&
      (P[id].sp === sp || P[id].sp === 'both') &&
      id !== 'vet' &&
      !(id in excluded) &&
      !(id in askFirst);

    // score
    const score = {},
      reasons = {};
    const add = (id, pts, why) => {
      if (!ok(id)) return;
      score[id] = (score[id] || 0) + pts;
      reasons[id] = reasons[id] || [];
      if (why && !reasons[id].some((r) => r.t === why.t)) reasons[id].push(why);
    };
    const concerns = a.concerns.length ? a.concerns : ['food'];
    concerns.forEach((c, ci) => {
      const w = [10, 6, 4][ci] || 3;
      (CONCERNS[c][sp] || []).forEach(([id, txt], pi) =>
        add(id, w - pi * 1.5, { c: CONCERNS[c].short, t: txt, rank: pi + ci * 0.5 }),
      );
    });
    // life stage + size + diet modifiers
    const senior = ageM >= 84,
      young = ageM < 12;
    if (senior) {
      add(sp === 'dog' ? 'dMob' : 'cVital', 3, {
        c: 'age',
        t: 'At ' + ageLabel(ageM) + ', joints and appetite usually need more support.',
      });
    }
    if (sp === 'dog' && JOINT_BREEDS.includes(a.breed) && concerns.includes('joints')) {
      add('dMob', 3, {
        c: 'breed',
        t: a.breed + 's are among the breeds the Mobility+ FAQ calls prone to joint issues.',
      });
    }
    if (sp === 'dog' && kg >= 25 && concerns.includes('food'))
      add('dBuff', 2, { c: 'size', t: 'Made with large and giant breeds in mind (FAQ).' });
    if (a.diet === 'kibble') {
      ['dBroth', 'cBroth15', 'cBroth', 'dTopper'].forEach((id) =>
        add(id, 2, {
          c: 'diet',
          t: 'You feed kibble. The broth FAQ says it is particularly useful as a topper for dry kibble.',
        }),
      );
    }
    if (a.diet === 'home' && concerns.includes('food')) {
      ['dTrial', 'cTrial'].forEach((id) => add(id, 1, null));
    }
    if (sp === 'dog' && allergies.includes('chicken')) {
      add('dBuff', 3, { c: 'allergy', t: 'No chicken anywhere: buffalo meat with buff bone broth.' });
      add('dLambR', 2, { c: 'allergy', t: 'No chicken anywhere: lamb with lamb bone broth.' });
      add('dLamb', 2, { c: 'allergy', t: 'Lamb bone broth, so it suits a chicken allergy.' });
    }
    if (sp === 'dog' && a.activity === 'high')
      add('dBuff', 2, { c: 'activity', t: 'The FAQ suggests Buff & Berry for active and working dogs.' });

    // pick a routine: hero, one add-on of a different format, one optional treat/care
    const ranked = Object.keys(score)
      .filter(ok)
      .sort((x, y) => score[y] - score[x]);
    // one broth (a kit counts as broth + Revive+), one meal, one treat; supplements may repeat if different
    const fam = (id) => {
      const f = P[id].fmt;
      return f === 'kit' || f === 'broth' ? 'broth' : f === 'supplement' ? id : f;
    };
    const picks = [];
    const used = new Set();
    const take = (id) => {
      if (picks.length >= 3 || picks.includes(id) || used.has(fam(id))) return false;
      picks.push(id);
      used.add(fam(id));
      if (P[id].fmt === 'kit') used.add(sp === 'dog' ? 'dRevive' : 'cRevive');
      return true;
    };
    // first, the best product for each goal, in goal order, so every goal is covered
    concerns.forEach((c) => {
      const list = (CONCERNS[c][sp] || []).map((x) => x[0]);
      const best = ranked.find((id) => list.includes(id) && !picks.includes(id) && !used.has(fam(id)));
      if (best) take(best);
    });
    // then fill by overall score, but only with products that serve a picked goal
    const goalIds = new Set(concerns.flatMap((c) => (CONCERNS[c][sp] || []).map((x) => x[0])));
    for (const id of ranked) if (goalIds.has(id)) take(id);
    picks.sort((x, y) => score[y] - score[x]);

    // vet notes
    const notes = [];
    if (flags.includes('kidney'))
      notes.push([
        'Kidney disease',
        'We have taken bone broth and every broth-based supplement out of ' +
          name +
          "'s plan. Bone is where most phosphorus sits, and kidney diets usually limit it. Please plan food changes with your vet.",
      ]);
    if (flags.includes('diabetes'))
      notes.push([
        'Diabetes',
        'Revive+ contains dextrose and maltodextrin, so it is out. Check any diet change with your vet.',
      ]);
    if (flags.includes('pancreas'))
      notes.push([
        'Low-fat diet',
        'Our broths and meals have no added fat, but please confirm portions with your vet first.',
      ]);
    if (flags.includes('meds'))
      notes.push([
        'On medication',
        'The product FAQs ask that a vet checks any new supplement for a pet under treatment. Take this plan to your next visit.',
      ]);
    if (flags.includes('preg'))
      notes.push([
        'Pregnant or nursing',
        'Nutrition needs change a lot right now. Please check supplements with your vet before starting.',
      ]);
    if (ageM < 3)
      notes.push([
        sp === 'dog' ? 'Very young puppy' : 'Very young kitten',
        "Our FAQs recommend starting solid food after 3 months. Until then, please follow your vet's feeding plan.",
      ]);
    if (allergies.length)
      notes.push([
        'Allergies',
        'We only recommend products whose published ingredient list is free of ' +
          allergies.join(', ') +
          '. Products without a full list are under "Ask us first".',
      ]);
    const needVet =
      flags.some((f) => ['kidney', 'diabetes', 'pancreas', 'meds', 'preg'].includes(f)) || ageM < 3;
    if (a.concerns.includes('recovery'))
      notes.push([
        'Recovery',
        'If vomiting or loose stools last more than 24 hours, see a vet. Revive+ supports recovery but does not replace treatment.',
      ]);

    // everyday extras: good for most pets, beyond the problems picked
    const extras = (EXTRAS[sp] || [])
      .filter(
        ([id, , cond]) =>
          ok(id) &&
          !soldOut.has(id) &&
          !picks.includes(id) &&
          !(picks.some((x) => P[x].fmt === 'kit') && P[id].fmt === 'broth') &&
          !(P[id].fmt === 'broth' && picks.some((x) => P[x].fmt === 'broth')) &&
          (!cond || cond(a)),
      )
      .sort((x, y) => (y[3] ? y[3](a) : 0) - (x[3] ? x[3](a) : 0))
      .slice(0, 2)
      .map(([id, why]) => [id, why]);

    return { picks, reasons, excluded, askFirst, notes, needVet, name, extras, soldOut };
  }

  /* ============================================================================
     SECTION 9 — DOSE TEXT + PACK/PRICE PER PRODUCT
     doseLine() = the "how much a day" line on each card. variantFor() = which Shopify
     variant (and price) goes in the bundle for the current "Try first / Value pack" mode.
     ============================================================================ */
  function doseLine(id, a) {
    const p = P[id],
      kg = a.kg,
      sp = a.species,
      nm = a.name || 'them';
    if (p.dose === 'mobility') {
      const big = kg > 25;
      return {
        txt: `<b>One ${big ? '50' : '25'} ml sachet a day</b> for ${nm} at ~${Math.round(kg)} kg`,
        src: 'Sachet sizes per the product listing (under/over 25 kg)',
        perMonth: { packs: 2, label: "a month's supply" },
      };
    }
    if (p.dose && DOSE[p.dose]) {
      const ml = bandFor(kg, DOSE[p.dose].bands);
      const days = p.packMl / ml;
      const packs = Math.ceil(30 / days);
      return {
        txt:
          `<b>Up to ${ml} ml a day</b> over food or as an appetiser. At that amount, one 100 ml pack lasts ${days >= 1 ? Math.round(days * 10) / 10 : 'under 1'} day${days === 1 ? '' : 's'}.` +
          (days > 3 ? ' Once open, use it within 72 hours.' : ''),
        src: DOSE[p.dose].src + ' · weight band for ~' + Math.round(kg) + ' kg',
        perMonth: { packs, label: `about ${packs} packs a month at the full daily amount` },
      };
    }
    if (sp === 'cat' && (p.fmt === 'broth' || p.fmt === 'kit')) {
      const R = RULES.catBroth;
      const ml = A.ageMonths < 12 ? R.kitten : bandFor(kg, R.bands);
      return {
        txt:
          `<b>Up to ${ml} ml a day</b> over food or on its own${A.ageMonths < 12 ? ' (kitten amount)' : ''}. Start smaller and build up.` +
          (p.packMl
            ? ` A ${p.packMl === 375 ? '15-shot box' : '3-pack'} lasts about ${Math.round(p.packMl / ml)} days at that amount.`
            : ''),
        src: R.src + ' · ~' + Math.round(kg) + ' kg',
        perMonth: null,
      };
    }
    if (id === 'dTopper') {
      const n = bandFor(kg, RULES.dogTopper.bands);
      const packs = Math.ceil((n * 30) / 4);
      return {
        txt: `<b>${n} pouch${n === 1 ? '' : 'es'} a day</b> over ${nm}'s usual food. A pack of 4 lasts ${Math.round(40 / n) / 10} days.`,
        src: RULES.dogTopper.src,
        perMonth: { packs, label: `about ${packs} packs a month` },
      };
    }
    if (p.rule === 'dental') {
      const n = bandFor(kg, RULES.dental.bands);
      return {
        txt: `<b>${n} stick${n > 1 ? 's' : ''} a day</b> for a dog of ~${Math.round(kg)} kg.`,
        src: RULES.dental.src,
        perMonth: null,
      };
    }
    if (p.rule === 'dogCubes') {
      const n = bandFor(kg, RULES.dogCubes.bands);
      return {
        txt: `<b>Up to ${n} cubes a day</b>, as rewards or crumbled over food.`,
        src: RULES.dogCubes.src,
        perMonth: null,
      };
    }
    if (p.rule === 'catCubes') {
      const n = bandFor(kg, RULES.catCubes.bands);
      return {
        txt: `<b>Up to ${n} cubes a day</b>, as rewards or crumbled over food.`,
        src: RULES.catCubes.src,
        perMonth: null,
      };
    }
    if (p.meal && sp === 'dog' && !p.topper && A.ageMonths >= 3) {
      const m = mealPlan(id);
      return { txt: m.txt, src: m.src, perMonth: m.perMonth };
    }
    if (p.meal && sp === 'cat' && p.guide && A.ageMonths >= 3) {
      const m = catMeal(id);
      return { txt: m.txt, src: m.src, perMonth: m.perMonth };
    }
    if (p.fmt === 'broth' || p.fmt === 'kit')
      return {
        txt: `<b>Start with 30–40 ml a day</b>, warm or at room temperature, poured over food or served on its own. Then adjust to how ${nm} responds.`,
        src: '"How should bone broth be served" FAQ',
        perMonth: null,
      };
    if (p.meal && sp === 'dog')
      return {
        txt: `Offer as a <b>full meal or as a topper</b> over the current bowl. A dog of ~${Math.round(kg)} kg eats once or twice a day, and very large breeds up to three times.`,
        src: '"How often should I feed my dog" FAQ. Gram amounts not yet published',
        perMonth: null,
      };
    if (p.meal && sp === 'cat')
      return {
        txt: `Serve at <b>room temperature</b>. Take refrigerated food out about 15 minutes before serving.`,
        src: '"How to serve wet cat food" FAQ. Gram amounts not yet published',
        perMonth: null,
      };
    if (p.fmt === 'treat')
      return {
        txt: `<b>A few pieces a day</b> as a reward or crumbled over food.`,
        src: 'Treats FAQ',
        perMonth: null,
      };
    if (p.noDose) {
      const ml = Math.max(0.5, Math.round((kg / 5) * 2) / 2);
      return {
        txt: `<b>About ${ml} ml a day</b>, mixed into food (1 ml per 5 kg), or as your vet advises.`,
        src: 'Hemp oil pack: "Mix 1 ml per 5 kg daily in food"',
        perMonth: null,
      };
    }
    return { txt: 'Apply a thin layer to clean, dry pads.', src: 'Product listing', perMonth: null };
  }

  /* Cat wet food: grams from each recipe's own PDP feeding guide (70 g pouches). */
  function catMeal(id) {
    const p = P[id],
      R = RULES[p.guide],
      kitten = A.ageMonths < 12 && R.kitten;
    const row = (kitten ? R.kitten : R.adult).find(([max]) => A.kg <= max);
    const [, lo, hi] = row;
    const pLo = lo / 70,
      pHi = hi / 70,
      mid = (lo + hi) / 2,
      month = Math.ceil((mid / 70) * 30);
    const pt = pLo === pHi ? `${pLo}` : `${pLo}–${pHi}`;
    const box = p.boxG
      ? ` (${Math.round((lo / p.boxG) * 10) / 10}${lo === hi ? '' : '–' + Math.round((hi / p.boxG) * 10) / 10} boxes of 210 g)`
      : '';
    return {
      txt: `<b>About ${lo === hi ? lo : lo + '–' + hi} g a day</b>, ${pt} pouch${pHi > 1 ? 'es' : ''} of 70 g${box}, as ${A.name ? esc(A.name) + "'s" : 'the'} main food. Split it over 2–3 meals.`,
      src:
        `${R.src} · ${kitten ? 'kitten' : 'adult'} row for ~${Math.round(A.kg)} kg` +
        (p.guide === 'catWhole' && A.kg > 8 ? '. Guide stops at 8 kg, so this uses the 4–8 kg row' : ''),
      perMonth: {
        packs: month,
        label: `about ${month} pouches (${Math.round((month * 70) / 100) / 10} kg) a month`,
      },
    };
  }
  /* Meal grams: 3–5% of bodyweight by activity; seniors and low activity 3%; puppies 4%; topper = half. */
  function mealPct() {
    if (A.ageMonths < 12) return [RULES.dogMeal.perKg.puppy, 'puppy'];
    if (A.ageMonths >= 84) return [RULES.dogMeal.perKg.low, 'senior'];
    return [RULES.dogMeal.perKg[A.activity || 'moderate'], A.activity || 'moderate'];
  }
  function mealPlan(id) {
    const p = P[id];
    const [pk, why] = mealPct();
    const f = FEED === 'topper' ? RULES.dogMeal.topper : 1;
    const lo = Math.round((A.kg * pk[0] * f) / 10) * 10,
      hi = Math.round((A.kg * pk[1] * f) / 10) * 10,
      mid = (lo + hi) / 2;
    if (id === 'dTrial') {
      const days = Math.max(1, Math.round(400 / mid));
      return {
        txt: `<b>About ${lo === hi ? lo : lo + '–' + hi} g a day</b> ${FEED === 'topper' ? 'as a topper' : 'as a full meal'}. The 4 × 100 g trial covers about ${days} day${days > 1 ? 's' : ''}, enough to find ${A.name ? esc(A.name) + "'s" : 'the'} favourite flavour.`,
        src: RULES.dogMeal.src,
        perMonth: null,
        grams: mid,
      };
    }
    const sizes = Object.keys(p.sizes)
      .map(Number)
      .sort((a, b) => b - a);
    const size = sizes.find((sz) => mid / sz >= 1.5) || sizes[sizes.length - 1];
    const pd = mid / size,
      pdLo = Math.max(1, Math.round(lo / size)),
      pdHi = Math.max(1, Math.round(hi / size));
    const month = Math.ceil(pd * 30);
    const pctTxt = pk[0] === pk[1] ? `${pk[0] / 10}%` : `${pk[0] / 10}–${pk[1] / 10}%`;
    const whyTxt = {
      puppy: 'a growing puppy',
      senior: 'a senior',
      low: 'a low-activity dog',
      moderate: 'a moderately active dog',
      high: 'a very active dog',
    }[why];
    return {
      size,
      month,
      grams: mid,
      txt: `<b>About ${lo === hi ? lo : lo + '–' + hi} g a day</b> ${FEED === 'topper' ? 'as a topper (half a full meal)' : 'as a full meal, split over 2–3 meals'}. That's <b>${pdLo === pdHi ? pdLo : pdLo + '–' + pdHi} pack${pdHi > 1 ? 's' : ''} of ${size} g</b> a day, about ${month} a month.`,
      src: `${RULES.dogMeal.src} · ${pctTxt} of ~${Math.round(A.kg)} kg for ${whyTxt}${FEED === 'topper' ? ', halved for a topper' : ''}`,
      perMonth: {
        packs: month,
        label: `about ${Math.ceil(month / 15)} box${Math.ceil(month / 15) > 1 ? 'es' : ''} of 15 a month`,
      },
    };
  }
  function variantFor(id) {
    const c = liveCands(id);
    if (c && c.length) {
      const want = (VSEL[id] || ['', ''])[mode === 'stock' ? 1 : 0];
      const av = c.filter((x) => x[3]);
      const v =
        av.find((x) => want && x[0].includes(want)) ||
        (mode === 'stock'
          ? av.slice().sort((a, b) => b[1] - a[1])[0]
          : av.slice().sort((a, b) => a[1] - b[1])[0]);
      if (v)
        return {
          price: v[1],
          mrp: v[2] && v[2] > v[1] ? v[2] : null,
          unit: v[0] === 'Default Title' ? '' : v[0],
          vid: v[4],
        };
    }
    const p0 = P[id];
    if (p0.sizes && A.species === 'dog') {
      const m = mealPlan(id);
      const pr = p0.sizes[m.size];
      const box = mode === 'stock' ? (pr[2] ? 15 : 7) : 1;
      const price = box === 1 ? pr[0] : box === 7 ? pr[1] : pr[2];
      return { price, unit: box === 1 ? `${m.size} g · single meal` : `${box} meals × ${m.size} g` };
    }
    const p = P[id];
    const base = p.large && A.kg > 25 ? p.large : p;
    const v = mode === 'stock' && base.bulk ? base.bulk : base;
    return { price: v.price, unit: v.unit, h: v.h, oos: p.smallOOS && !(A.kg > 25) };
  }
  function ageLabel(m) {
    if (m < 3) return 'under 3 months';
    if (m < 12) return 'under a year';
    if (m < 84) return '1–7 years';
    return '7+ years';
  }

  /* =====================================================================
   QUIZ UI
   ===================================================================== */
  /* ============================================================================
     SECTION 10 — QUIZ STATE + QUESTIONS (STEPS)
     A = the customer's answers. step = current screen. mode = try|stock (pack size).
     STEPS[] below: each has render() (HTML), bind() (click handlers), optional
     bar() (bottom Continue button) and skip() (e.g. activity is dogs only).
     ============================================================================ */
  const root = document.getElementById('quiz-root');
  const HOST = () => root.closest('.gtq') || document.body;
  let A = {
    species: null,
    name: '',
    ageMonths: null,
    breed: '',
    band: null,
    kg: null,
    kgFromBreed: false,
    diet: null,
    activity: null,
    concerns: [],
    flags: [],
    other: {},
  };
  let step = 0,
    dirn = 1,
    mode = 'try',
    FEED = null;
  const SKIP = new Set(),
    ADDED = new Set();
  const esc = (s) =>
    String(s).replace(
      /[&<>"']/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c],
    );
  const nm = () => (A.name ? esc(A.name) : A.species === 'cat' ? 'your cat' : 'your dog');
  const Nm = () => (A.name ? esc(A.name) : A.species === 'cat' ? 'Your cat' : 'Your dog');
  const he = () => (A.name ? esc(A.name) : 'they');

  /* ---------- icons: one line family, 24px grid, 2px stroke, round caps (brand book §4, tier 1) ---------- */
  const IC = {
    back: '<path d="M19 12H5M12 19l-7-7 7-7"/>',
    ext: '<path d="M7 17 17 7M8 7h9v9"/>',
    check: '<path d="M20 6 9 17l-5-5"/>',
    plus: '<path d="M12 5v14M5 12h14"/>',
    food: '<path d="M3 11h18a9 9 0 0 1-18 0Z"/><path d="M7 21h10"/><path d="M9 7c0-1.2 1-1.6 1-2.8M14 7c0-1.2 1-1.6 1-2.8"/>',
    fussy: '<path d="M7 3v7a2 2 0 0 0 4 0V3M9 10v11M17 21V3c-1.9 0-3 2.4-3 5.5S15.1 13 17 13"/>',
    hydration: '<path d="M12 2.7s-6 6.6-6 11.3a6 6 0 0 0 12 0c0-4.7-6-11.3-6-11.3Z"/>',
    tummy: '<path d="M5 19c0-8.3 5.4-14 15-14 0 9.6-5.7 15-14 15"/><path d="M5 19l7.5-7.5"/>',
    joints:
      '<path d="M17 10c.7-.7 1.7-.6 2.4-1.4a2 2 0 0 0-2.8-2.8c.1-1.1-.2-2.1-1.3-2.5a2 2 0 0 0-2.3 2.9l-5.8 5.9a2 2 0 0 0-2.9 2.3c.4 1.1 1.4 1.4 2.5 1.3a2 2 0 0 0 2.8 2.8c.8-.7.7-1.7 1.4-2.4Z"/>',
    coat: '<path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6Z"/><path d="M18.5 14.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7Z"/>',
    dental:
      '<path d="M7.5 3C5.3 3 4 4.7 4 7c0 2.6 1.4 4 1.9 6.5.5 2.6.8 7.5 2.4 7.5 1.7 0 1.5-4.6 3.7-4.6s2 4.6 3.7 4.6c1.6 0 1.9-4.9 2.4-7.5C18.6 11 20 9.6 20 7c0-2.3-1.3-4-3.5-4-1.9 0-2.6 1-4.5 1S9.4 3 7.5 3Z"/>',
    recovery:
      '<path d="M20.8 8.6a5 5 0 0 0-8.8-3.2 5 5 0 0 0-8.8 3.2c0 5.4 8.8 11.4 8.8 11.4s8.8-6 8.8-11.4Z"/><path d="M3.6 12h4l2-3 3 6 2-3h5.8"/>',
    truck:
      '<path d="M3 6h11v10H3zM14 9h4l3 3v4h-7"/><circle cx="7" cy="18" r="1.8"/><circle cx="17" cy="18" r="1.8"/>',
  };
  const ic = (k, cls = 'ic') => `<svg class="${cls}" viewBox="0 0 24 24" aria-hidden="true">${IC[k]}</svg>`;
  /* Tier 2 brand illustration: line pets, real anatomy, 3px stroke, single brand colour */
  const PET = {
    dog: '<svg class="pet" viewBox="0 0 96 96" aria-hidden="true"><path d="M30 30c-9-4-17 1-18 12-1 8 3 15 9 16"/><path d="M66 30c9-4 17 1 18 12 1 8-3 15-9 16"/><path d="M30 30c5-5 11-7 18-7s13 2 18 7c5 6 6 15 5 24-1 12-11 20-23 20S26 66 25 54c-1-9 0-18 5-24Z"/><circle cx="39" cy="46" r="2.2" class="f"/><circle cx="57" cy="46" r="2.2" class="f"/><path d="M43 58h10l-5 5Z" class="f"/><path d="M48 63v4M42 69c3 2 9 2 12 0"/></svg>',
    cat: '<svg class="pet" viewBox="0 0 96 96" aria-hidden="true"><path d="M22 44V16l17 13M74 44V16L57 29"/><path d="M22 44c0 18 12 30 26 30s26-12 26-30c0-9-6-15-12-16-4-1-9-1-14-1s-10 0-14 1c-6 1-12 7-12 16Z"/><path d="M37 46c2-2 5-2 7 0M52 46c2-2 5-2 7 0"/><path d="M45 55h6l-3 3Z" class="f"/><path d="M48 58v3M43 63c3 2 7 2 10 0"/><path d="M12 52l18 1M12 60l18-3M84 52l-18 1M84 60l-18-3"/></svg>',
  };
  const CICON = {
    food: 'food',
    fussy: 'fussy',
    hydration: 'hydration',
    tummy: 'tummy',
    joints: 'joints',
    coat: 'coat',
    dental: 'dental',
    recovery: 'recovery',
  };
  const slug = (s) =>
    String(s || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  const stageOf = () => (A.ageMonths < 12 ? 'young' : A.ageMonths >= 84 ? 'senior' : 'adult');
  /* Pet photo slot: img/pets/<species>-<breed>-<young|adult|senior>.jpg. Falls back to the line illustration until photos exist. */
  function petPhoto(cls = 'petph') {
    const f = `${GTQ.petBase || 'img/pets/'}${A.species}-${slug(A.breed || 'mixed')}-${stageOf()}.jpg`;
    return `<div class="${cls}">${PET[A.species || 'dog']}<img src="${f}" alt="${esc(A.breed || A.species)}, ${{ young: A.species === 'cat' ? 'kitten' : 'puppy', adult: 'adult', senior: 'senior' }[stageOf()]}" loading="lazy" onerror="this.remove()"></div>`;
  }
  const inr = (n) => '<span class="rs">₹</span>' + Math.round(n).toLocaleString('en-IN');

  const STEPS = [
    {
      id: 'species',
      render() {
        return `
  <p class="kicker">Pet plan · about 2 minutes</p>
  <h1 tabindex="-1">Who are we <em>building this plan</em> for?</h1>
  <p class="help">Eight short questions. We match broth, supplements, meals and treats to your pet's age, size and needs, and leave out anything that isn't right for them.</p>
  <div class="opts two">
   ${['dog', 'cat'].map((s) => `<button class="opt big" data-v="${s}" aria-pressed="${A.species === s}">${PET[s]}<span class="t">${s === 'dog' ? 'My dog' : 'My cat'}</span></button>`).join('')}
  </div>
  <p class="hint">Dog and cat at home? Do one now, then add the other from the results page.</p>
  ${
    GTQ.public
      ? ''
      : `<div class="sample"><span>See a finished plan first:</span>
    <button class="link" data-sample="dog">Bruno, 8-year-old Labrador</button>
    <button class="link" data-sample="cat">Mishti, picky Persian kitten</button>
    <button class="link" data-sample="coco">Coco, active Indie with a chicken allergy</button></div>`
  }`;
      },
      bind() {
        root.querySelectorAll('[data-v]').forEach(
          (b) =>
            (b.onclick = () =>
              pick(b, () => {
                if (A.species !== b.dataset.v) {
                  A.breed = '';
                  A.band = null;
                  A.kg = null;
                  A.concerns = [];
                  A.activity = null;
                }
                A.species = b.dataset.v;
              })),
        );
        root
          .querySelectorAll('[data-sample]')
          .forEach((b) => (b.onclick = () => loadSample(b.dataset.sample)));
      },
    },
    {
      id: 'name',
      bar: () => ({ label: 'Continue', ok: true, note: '' }),
      render() {
        return `
  <h1 tabindex="-1">What's your ${A.species}'s <em>name?</em></h1>
  <p class="help">We'll use it in the plan, so it reads like it was written for them.</p>
  <div class="field"><label for="in-name">Name</label><input id="in-name" class="inp" maxlength="24" autocomplete="off" enterkeyhint="next" placeholder="${A.species === 'dog' ? 'e.g. Bruno' : 'e.g. Mishti'}" value="${esc(A.name)}">
  <p class="hint">Optional. <button class="link" id="skip">Skip this</button></p></div>`;
      },
      bind() {
        const i = root.querySelector('#in-name');
        i.oninput = () => {
          A.name = i.value.trim();
        };
        i.onkeydown = (e) => {
          if (e.key === 'Enter') {
            A.name = i.value.trim();
            next();
          }
        };
        root.querySelector('#skip').onclick = () => {
          A.name = '';
          next();
        };
      },
    },
    {
      id: 'age',
      bar: () => ({ label: 'Continue', ok: A.ageMonths != null, note: '' }), // Continue button (17 Oct)
      render() {
        const yo = A.species === 'dog' ? 'Puppy' : 'Kitten';
        const o = [
          [1, 'Under 3 months', yo + ', still very young'],
          [8, '3 to 12 months', yo],
          [36, '1 to 7 years', 'Adult'],
          [96, '7 years or older', 'Senior'],
        ];
        return `
  <h1 tabindex="-1">How old is <em>${nm()}?</em></h1>
  <p class="help">Age changes what we suggest. Our meals start after 3 months, and seniors get more joint support.</p>
  <div class="opts">${o.map(([m, t, s]) => `<button class="opt" data-v="${m}" aria-pressed="${A.ageMonths === m}"><span><span class="t">${t}</span><span class="s">${s}</span></span></button>`).join('')}</div>`;
      },
      bind() {
        root.querySelectorAll('[data-v]').forEach(
          (b) =>
            (b.onclick = () =>
              pick(b, () => {
                A.ageMonths = +b.dataset.v;
              })),
        );
      },
    },
    {
      id: 'size',
      bar: () => ({ label: 'Continue', ok: !!A.band, note: A.band ? '' : 'Choose a weight' }),
      render() {
        const br = BREEDS[A.species];
        const wb = WBANDS[A.species];
        return `
  <h1 tabindex="-1">${Nm()}'s <em>breed and size</em></h1>
  <p class="help">Weight sets the daily amount of every supplement and meal. Type a breed and we'll fill in a typical weight. You can change it. Not listed? Type it in and add it.</p>
  <div class="field combo"><label for="in-breed">Breed</label>
   <input id="in-breed" class="inp" role="combobox" aria-expanded="false" aria-controls="breed-list" aria-autocomplete="list" autocomplete="off" spellcheck="false" placeholder="Type to search, e.g. ${A.species === 'dog' ? 'Labrador' : 'Persian'}" value="${esc(A.breed)}">
   <ul id="breed-list" class="combo-list" role="listbox" aria-label="Breeds" hidden></ul>
   ${A.breedCustom ? `<p class="hint">Got it: ${esc(A.breed)}. Pick a weight below so the amounts are right.</p>` : ''}</div>
  <fieldset class="field"><legend>Weight</legend>
   <div class="opts two">${wb.map(([k, t]) => `<button class="opt" data-w="${k}" aria-pressed="${A.band === k}"><span class="t">${t}</span></button>`).join('')}</div>
   <p class="hint" aria-live="polite">${A.kgFromBreed && A.breed ? `*Weight is indicative: a typical adult ${esc(A.breed)}. Tap another band if ${he()} is lighter or heavier.` : '*Weight is indicative. Pick the band closest to your pet.'}</p></fieldset>`;
      },
      bind() {
        breedCombo(root.querySelector('#in-breed'), root.querySelector('#breed-list'));
        root.querySelectorAll('[data-w]').forEach(
          (b) =>
            (b.onclick = () => {
              const w = WBANDS[A.species].find((x) => x[0] === b.dataset.w);
              A.band = w[0];
              A.kg = w[2];
              A.kgFromBreed = false;
              render(true);
            }),
        );
      },
    },
    {
      id: 'activity',
      bar: () => ({ label: 'Continue', ok: !!A.activity, note: '' }), // Continue button (17 Oct)
      skip: () => A.species !== 'dog',
      render() {
        const o = [
          ['low', 'Takes it easy', 'Short walks, mostly lounging'],
          ['moderate', 'Moderately active', 'Daily walks and some play'],
          ['high', 'Very active', 'Runs, sport or working, 2+ hours a day'],
        ];
        return `
  <h1 tabindex="-1">How active is <em>${nm()}?</em></h1>
  <p class="help">Activity sets how much fresh food ${he()} needs: 3% of body weight for a calm dog, up to 5% for a very active one.${A.ageMonths >= 84 ? ' Seniors use the 3% amount whatever their activity.' : ''}</p>
  <div class="opts">${o.map(([v, t, sub]) => `<button class="opt" data-v="${v}" aria-pressed="${A.activity === v}"><span><span class="t">${t}</span><span class="s">${sub}</span></span></button>`).join('')}</div>`;
      },
      bind() {
        root.querySelectorAll('[data-v]').forEach(
          (b) =>
            (b.onclick = () =>
              pick(b, () => {
                A.activity = b.dataset.v;
              })),
        );
      },
    },
    {
      id: 'diet',
      render() {
        const o = [
          ['kibble', 'Dry kibble', 'Packaged dry food'],
          ['home', 'Home-cooked', 'Rice, roti, chicken, curd and so on'],
          ['wet', 'Wet or fresh food', 'Gravies, pouches or fresh meals'],
          ['mix', 'A mix of these', ''],
        ];
        return `
  <h1 tabindex="-1">What's in ${nm()}'s <em>bowl</em> today?</h1>
  <p class="help">This decides whether we suggest a topper for the food ${he()} already eats or a meal switch.</p>
  ${otherBox('diet', 'Not listed? Tell us here', 'e.g. raw food, curd rice, only treats')}
  <div class="opts">${o.map(([v, t, s]) => `<button class="opt" data-v="${v}" aria-pressed="${A.diet === v}"><span><span class="t">${t}</span>${s ? `<span class="s">${s}</span>` : ''}</span></button>`).join('')}</div>`;
      },
      bar: () => ({ label: 'Continue', ok: !!A.diet || !!(A.other && A.other.diet), note: '' }),
      bind() {
        root.querySelectorAll('[data-v]').forEach(
          (b) =>
            (b.onclick = () =>
              pick(b, () => {
                A.diet = b.dataset.v;
              })),
        );
        bindOther('diet', () => {
          if (!A.diet && A.other.diet) A.diet = 'mix';
          refreshBar();
        });
      },
    },
    {
      id: 'concerns',
      bar: () => ({
        label: 'Continue',
        ok: A.concerns.length > 0 || !!(A.other && A.other.concern),
        note: A.concerns.length
          ? `${A.concerns.length} of 3 picked`
          : A.other && A.other.concern
            ? ''
            : 'Pick at least one, or tell us above',
      }),
      render() {
        const keys = ['food', 'fussy', 'hydration', 'tummy', 'joints', 'coat', 'dental', 'recovery'];
        return `
  <h1 tabindex="-1">What would you most like to <em>help ${nm()} with?</em></h1>
  <p class="help">Pick up to 3, most important first. The number shows the order we plan around.</p>
  ${otherBox('concern', 'Not listed? Tell us here', 'e.g. tear stains, anxiety on walks')}
  <div class="tiles">${keys
    .map((k) => {
      const i = A.concerns.indexOf(k);
      const full = A.concerns.length >= 3 && i < 0;
      return `<button class="tile" data-v="${k}" aria-pressed="${i > -1}" ${full ? 'aria-disabled="true"' : ''}>${ic(CICON[k])}<span class="t">${CONCERNS[k].label}</span><span class="s">${CONCERNS[k].sub}</span><span class="badge" aria-hidden="true">${i > -1 ? i + 1 : ''}</span></button>`;
    })
    .join('')}</div>`;
      },
      bind() {
        root.querySelectorAll('[data-v]').forEach(
          (b) =>
            (b.onclick = () => {
              const k = b.dataset.v;
              const i = A.concerns.indexOf(k);
              if (i > -1) A.concerns.splice(i, 1);
              else if (A.concerns.length < 3) A.concerns.push(k);
              render(true);
            }),
        );
        bindOther('concern', refreshBar);
      },
    },
    {
      id: 'flags',
      bar: () => ({
        label: `See ${nm()}'s plan`,
        ok: A.flags.length > 0 || !!(A.other && A.other.flag),
        note: A.flags.length || (A.other && A.other.flag) ? '' : 'Pick one, or "None of these"',
      }),
      render() {
        const grp = [
          ['Allergies', 'allergy'],
          ['Health conditions', 'med'],
          ['Right now', 'vet'],
        ];
        return `
  <h1 tabindex="-1">Anything we <em>must avoid?</em></h1>
  <p class="help">The most important question. We remove every product with a matching ingredient.</p>
  ${otherBox('flag', 'Not listed? Tell us here', 'e.g. pork allergy, epilepsy, after surgery')}
  ${grp
    .map(
      ([lg, kind]) =>
        `<fieldset class="grp"><legend>${lg}</legend><div class="chips">${FLAGS.filter((f) => f[2] === kind)
          .map(
            ([k, t]) =>
              `<button class="chipbtn" data-v="${k}" aria-pressed="${A.flags.includes(k)}">${A.flags.includes(k) ? ic('check', 'ic sm') : ''}${t}</button>`,
          )
          .join('')}</div></fieldset>`,
    )
    .join('')}
  <button class="opt none" data-v="none" aria-pressed="${A.flags.includes('none')}"><span class="t">None of these</span></button>`;
      },
      bind() {
        root.querySelectorAll('[data-v]').forEach(
          (b) =>
            (b.onclick = () => {
              const k = b.dataset.v;
              if (k === 'none') A.flags = A.flags.includes('none') ? [] : ['none'];
              else {
                A.flags = A.flags.filter((f) => f !== 'none');
                A.flags.includes(k) ? (A.flags = A.flags.filter((f) => f !== k)) : A.flags.push(k);
              }
              render(true);
            }),
        );
        bindOther('flag', refreshBar);
      },
    },
  ];

  /* ============================================================================
     SECTION 11 — SCREEN RENDERING HELPERS
     Breed search box, "Not listed? Tell us here" inputs, render() / next() navigation.
     ============================================================================ */
  /* Breed type-ahead: matches names and common aliases (Lab, GSD, Desi…); "Not listed" adds the typed breed. */
  function breedMatches(q) {
    const list = BREEDS[A.species];
    q = q.trim().toLowerCase();
    if (!q) return list.filter((b) => b[4]).slice(0, 8);
    const sc = (b) => {
      const n = b[0].toLowerCase(),
        al = (b[3] || '').toLowerCase();
      if (n.startsWith(q)) return 0;
      if (n.split(/[\s/()-]+/).some((w) => w.startsWith(q))) return 1;
      if (al.split(' ').some((w) => w.startsWith(q)) || al.includes(q)) return 2;
      return 9;
    };
    return list
      .map((b) => [b, sc(b)])
      .filter((x) => x[1] < 9)
      .sort((a, b) => a[1] - b[1] || b[0][4] - a[0][4] || a[0][0].localeCompare(b[0][0]))
      .map((x) => x[0])
      .slice(0, 8);
  }
  function selectBreed(name, custom) {
    A.breed = name;
    A.breedCustom = !!custom;
    if (!custom) {
      const b = BREEDS[A.species].find((x) => x[0] === name);
      const mid = (b[1] + b[2]) / 2;
      const wb = WBANDS[A.species];
      const band = wb.find(([k, t, kg], i) => {
        const lim = A.species === 'dog' ? [10, 20, 40, 999][i] : [5, 10, 999][i];
        return mid <= lim;
      });
      A.band = band[0];
      A.kg = mid;
      A.kgFromBreed = true;
    } else {
      A.kgFromBreed = false;
      document.dispatchEvent(
        new CustomEvent('gtq:newbreed', { detail: { species: A.species, breed: name } }),
      );
    }
    render(true);
  }
  function breedCombo(inp, ul) {
    let opts = [],
      act = -1;
    const draw = () => {
      const q = inp.value;
      const m = breedMatches(q);
      const exact = m.some((b) => b[0].toLowerCase() === q.trim().toLowerCase());
      opts = m.map((b) => ({ name: b[0], label: esc(b[0]), meta: `${b[1]}–${b[2]} kg*` }));
      if (q.trim().length > 1 && !exact)
        opts.unshift({
          name: q.trim().replace(/\s+/g, ' ').slice(0, 40),
          custom: 1,
          label: `Not listed? Add “${esc(q.trim().slice(0, 40))}”`,
          meta: '',
        });
      if (!q.trim())
        opts.unshift({
          hint: 1,
          top: 1,
          label: "Not listed? Type your pet's breed above and add it",
          meta: '',
        });
      opts.push({ hint: 1, label: "*Weight is indicative. Pick your pet's actual weight below.", meta: '' });
      ul.innerHTML = opts
        .map(
          (o, k) =>
            `<li role="option" id="bo-${k}" ${o.hint ? `aria-disabled="true" class="hintrow${o.top ? ' top' : ''}"` : ''}${o.custom ? ' class="add"' : ''} aria-selected="${act >= 0 && opts.filter((o) => !o.hint)[act] === o}"><span>${o.label}</span><span class="wt">${o.meta}</span></li>`,
        )
        .join('');
      ul.hidden = false;
      inp.setAttribute('aria-expanded', 'true');
      inp.setAttribute('aria-activedescendant', act >= 0 ? 'bo-' + act : '');
      ul.querySelectorAll('li').forEach(
        (li, k) =>
          (li.onmousedown = (e) => {
            e.preventDefault();
            if (!opts[k].hint) selectBreed(opts[k].name, opts[k].custom);
          }),
      );
    };
    const close = () => {
      ul.hidden = true;
      inp.setAttribute('aria-expanded', 'false');
      act = -1;
    };
    inp.onfocus = () => {
      act = -1;
      draw();
    };
    inp.oninput = () => {
      act = -1;
      draw();
    };
    inp.onblur = () => setTimeout(close, 120);
    inp.onkeydown = (e) => {
      const n = opts.filter((o) => !o.hint).length;
      const real = opts.filter((o) => !o.hint);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        act = Math.min(n - 1, act + 1);
        draw();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        act = Math.max(0, act - 1);
        draw();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const o =
          act >= 0
            ? opts.filter((o) => !o.hint)[act]
            : opts.find((o) => !o.hint && !o.custom) || opts.find((o) => o.custom);
        if (o && !o.hint) selectBreed(o.name, o.custom);
      } else if (e.key === 'Escape') close();
    };
  }

  /* "Not listed? Tell us here": free text at the top of every list question. Stored in A.other, sent with the lead. */
  function otherBox(key, label, ph) {
    const v = (A.other && A.other[key]) || '';
    return `<div class="other"><label for="ot-${key}">${label}</label><input id="ot-${key}" class="inp" maxlength="80" autocomplete="off" placeholder="${ph}" value="${esc(v)}"></div>`;
  }
  function bindOther(key, after) {
    const i = root.querySelector('#ot-' + key);
    if (!i) return;
    i.oninput = () => {
      A.other = A.other || {};
      A.other[key] = i.value.trim();
      if (after) after();
    };
  }
  function refreshBar() {
    const s = STEPS[step];
    const g = root.querySelector('#go');
    if (!s || !s.bar || !g) return;
    const b = s.bar();
    g.disabled = !b.ok;
    const n = root.querySelector('.ab-note');
    if (n) n.textContent = b.note;
  }

  /* Shown at the foot of every screen. */
  const DISC = (cls = '') =>
    `<p class="disc-all ${cls}"><b>A starting point, not a diagnosis.</b> We match products to your answers and to what suits most pets. If your pet has an allergy, illness or condition we didn't ask about, or takes regular medication, please check with your vet before serving anything new.</p>`;

  /* single-select: show the pressed state, then move on */
  function pick(btn, apply) {
    if (busy) return;
    busy = true;
    apply();
    root.querySelectorAll('[aria-pressed]').forEach((b) => {
      if (b.dataset.v) b.setAttribute('aria-pressed', b === btn);
    });
    // Screens with a Continue button (bar) wait for Continue; others (e.g. My dog / My cat) move on by themselves.
    if (STEPS[step] && STEPS[step].bar) {
      busy = false;
      refreshBar();
      return;
    }
    setTimeout(
      () => {
        busy = false;
        next();
      },
      RM ? 0 : 170,
    );
  }
  let busy = false;
  const RM = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;

  function render(stay) {
    while (step >= 0 && step < STEPS.length && STEPS[step].skip && STEPS[step].skip()) step += dirn;
    if (step < 0) step = 0;
    if (step >= STEPS.length) {
      if (story < 9 && !A.sample) {
        if (story < 0) story = 0;
        renderStory();
        return;
      }
      renderResult();
      return;
    }
    A.sample = 0;
    const y = window.scrollY;
    const s = STEPS[step];
    const vis = STEPS.filter((x) => !(x.skip && x.skip()));
    const vi = vis.indexOf(s);
    const pct = Math.round((vi / vis.length) * 100);
    const bar = s.bar ? s.bar() : null;
    root.classList.remove('is-res');
    root.innerHTML = `<div class="prog"><button class="back" id="back" ${step === 0 ? 'disabled' : ''} aria-label="Back to the previous question">${ic('back')}<span>Back</span></button><div class="prog-bar" role="progressbar" aria-valuemin="0" aria-valuemax="${vis.length}" aria-valuenow="${vi}" aria-label="Question ${vi + 1} of ${vis.length}"><i style="width:${Math.max(pct, 4)}%"></i></div><span class="prog-txt">${vi + 1} of ${vis.length}</span></div>
   <div class="q${stay ? '' : ' enter'}">${s.render()}</div>
   ${DISC()}
   ${bar ? `<div class="actionbar"><div class="wrap ab-in"><span class="ab-note" aria-live="polite">${bar.note}</span><button class="cta" id="go" ${bar.ok ? '' : 'disabled'}>${bar.label}</button></div></div>` : ''}`;
    HOST().classList.toggle('has-bar', !!bar);
    root.querySelector('#back').onclick = () => {
      dirn = -1;
      step = Math.max(0, step - 1);
      render();
      dirn = 1;
    };
    const go = root.querySelector('#go');
    if (go) go.onclick = () => next();
    s.bind();
    save();
    if (stay) window.scrollTo({ top: y });
    else {
      const h = root.querySelector('h1');
      if (h && started) h.focus({ preventScroll: true });
    }
    started = true;
  }
  let started = false;
  function next() {
    step++;
    window.scrollTo({ top: 0 });
    if (step === STEPS.length) {
      HOST().classList.remove('has-bar');
      root.innerHTML = `<div class="building" role="status"><p class="kicker">One moment</p><h1>Building ${nm()}'s plan…</h1><ul><li>Checking ${A.flags.includes('none') ? 'ingredients' : 'ingredients against ' + A.flags.length + ' answer' + (A.flags.length > 1 ? 's' : '')}</li><li>Setting amounts for ~${Math.round(A.kg)} kg</li><li>Matching ${A.concerns.length} goal${A.concerns.length > 1 ? 's' : ''} to what's in stock today</li></ul></div>`;
      setTimeout(render, RM ? 0 : 1100);
      return;
    }
    render();
  }

  /* product photo with tap-to-browse when the site has more than one */
  function shot(id, cls = 'shot') {
    const p = P[id],
      im = liveImgs(id);
    return `<button class="${cls}" type="button" data-shot="${id}" data-i="0" aria-label="${im.length > 1 ? 'Next photo of ' : ''}${esc(liveTitle(id))}" ${im.length > 1 ? '' : 'tabindex="-1"'}><span class="pack p-${p.fmt}" aria-hidden="true"><span class="f">${FMT_LABEL[p.fmt]}</span><span class="n">${esc(p.short)}</span></span><img src="${im[0]}" alt="${esc(liveTitle(id))}" width="480" height="480" loading="lazy" onerror="this.remove()">${im.length > 1 ? `<span class="dots" aria-hidden="true">${im.map((_, k) => `<i${k ? '' : ' class="on"'}></i>`).join('')}</span>` : ''}</button>`;
  }
  function priceLine(v) {
    return `<span class="price">${inr(v.price)}</span>${v.mrp ? `<s class="mrp">${inr(v.mrp)}</s><span class="off">${Math.round((1 - v.price / v.mrp) * 100)}% off</span>` : ''}`;
  }

  /* =====================================================================
   SCIENCE — the screens after the recommendation (Seed-style).
   Every line carries a source. ok:false = draft wording that needs vet
   sign-off: shown with a "Draft" tag in the demo, hidden in Shopify
   unless GTQ.showDrafts is set.
   ===================================================================== */
  /* ============================================================================
     SECTION 12 — SCIENCE SCREENS (plan reveal copy)
     Lines with false at the end are DRAFT wording — hidden on Shopify until vets approve
     and "Show draft timeline lines" is ticked in the theme editor.
     ============================================================================ */
  const SCI_FAM = {
    dBroth: 'broth',
    dLamb: 'broth',
    cBroth: 'broth',
    cBroth15: 'broth',
    cSea: 'broth',
    dKit: 'kit',
    cKit: 'kit',
    dRevive: 'revive',
    cRevive: 'revive',
    dVital: 'vital',
    cVital: 'vital',
    dMob: 'mobility',
    dQuinoa: 'dogmeal',
    dHerbs: 'dogmeal',
    dLambR: 'dogmeal',
    dBuff: 'dogmeal',
    dTrial: 'dogmeal',
    cTrial: 'catmeal',
    cTrout: 'catmeal',
    cMackC: 'catmeal',
    cFlax: 'catmeal',
    cTuna: 'catmeal',
    c15: 'catmeal',
    cMackS: 'catmeal',
    dLiver: 'fd',
    cLiver: 'fd',
    cShrimp: 'fd',
    cTunaT: 'fd',
    dDental: 'dental',
    dDentalV: 'dental',
    dTwist: 'dental',
    omega: 'omega',
    dPaw: 'paw',
    cPaw: 'paw',
  };
  const SCIENCE = {
    broth: {
      label: 'Bone broth',
      meet: [
        ['hydration', 'Adds water to every meal', 'Broth is a liquid served over food (product FAQ)'],
        ['joints', 'Naturally occurring collagen and amino acids', 'Bone broth FAQ'],
        ['fussy', 'Makes meals more appealing', '"Why add bone broth" FAQ'],
      ],
      expect: [
        [
          'Day 1',
          'More water in every bowl',
          'A 25–80 ml serving is liquid poured over food.',
          'Feeding guide',
          true,
        ],
        [
          'Week 1',
          'Bowls finished more often',
          'Smell and moisture are what move a fussy eater.',
          'Draft wording',
          false,
        ],
        [
          'Week 4',
          'Part of the daily routine',
          'Serve daily or as a regular topper, as the FAQ suggests.',
          '"How often" FAQ',
          true,
        ],
      ],
      made: {
        title: 'Simmered for 24 hours, not 2.',
        sub: 'Soup is boiled for 1–2 hours. Our broth simmers bones for about 24 hours.',
        call: [
          ['24-hour simmer', 'pulls collagen out of the bones'],
          ['Apple cider vinegar', 'helps draw out minerals while it cooks'],
          ['Fat skimmed', 'no added fat or salt'],
          ['Steam-sealed', 'keeps unopened, no fridge needed'],
        ],
        src: 'Bone broth FAQs on goofytails.com',
      },
    },
    kit: { alias: 'broth' },
    revive: {
      label: 'Recovery supplement',
      meet: [
        ['hydration', 'Restores hydration and minerals', 'Revive+ FAQ'],
        ['tummy', 'Prebiotic inulin for the gut', 'Revive+ ingredients'],
        ['recovery', 'Supports energy and recovery', 'Revive+ FAQ'],
      ],
      expect: [
        [
          'Day 1',
          'Fluids and electrolytes, poured over food',
          'Served as a topper or appetiser, by weight.',
          'Revive+ FAQ',
          true,
        ],
        ['Day 2–3', 'Eating again', 'The FAQ says it encourages eating.', 'Revive+ FAQ', true],
        [
          'If not better in 24 h',
          'See a vet',
          'Revive+ supports recovery but does not replace treatment.',
          'Meals FAQ',
          true,
        ],
      ],
      made: {
        title: 'A recovery drink built on real broth.',
        sub: 'Chicken bone broth is the base. Everything else is added for a reason.',
        call: [
          ['Chicken bone broth', 'the base: fluid and protein'],
          ['Collagen peptides', 'amino acids for tissue repair'],
          ['Inulin', 'prebiotic fibre for the gut'],
          ['Dextrose + maltodextrin', 'quick, gentle energy'],
        ],
        src: 'Revive+ ingredient list',
      },
    },
    vital: {
      label: 'Daily wellness supplement',
      meet: [
        ['recovery', 'Ashwagandha for energy and calm', 'Vitality+ FAQ'],
        ['tummy', 'Fenugreek fibre, a prebiotic', 'Vitality+ ingredients'],
        ['coat', 'Collagen for skin and coat', 'Vitality+ FAQ'],
      ],
      expect: [
        [
          'Week 1',
          'Eats it happily',
          'It is a broth-based topper, made for fussy eaters.',
          'Vitality+ FAQ',
          true,
        ],
        [
          'Week 2–3',
          'More energy on walks',
          'Ashwagandha and turmeric support stamina.',
          'Draft wording',
          false,
        ],
        ['Week 4+', 'Shinier coat', 'Collagen supports skin and coat.', 'Draft wording', false],
      ],
      made: {
        title: 'Five actives in a broth base.',
        sub: 'Chicken bone broth carries everything, so it tastes like food, not medicine.',
        call: [
          ['Chicken bone broth', 'the base your pet actually wants to eat'],
          ['Ashwagandha', 'stamina and calm'],
          ['Turmeric + boswellia', 'everyday inflammation support'],
          ['Fenugreek fibre', 'prebiotic for the gut'],
        ],
        src: 'Vitality+ ingredient list and FAQ',
      },
    },
    mobility: {
      label: 'Joint supplement',
      meet: [
        ['joints', 'Glucosamine and chondroitin for cartilage', 'Mobility+ FAQ'],
        ['joints', 'Collagen types I, II and III', 'Mobility+ FAQ'],
        ['coat', 'Green-lipped mussel, turmeric and boswellia', 'Mobility+ FAQ'],
      ],
      expect: [
        ['Week 1', 'One sachet a day, over food', '25 ml under 25 kg, 50 ml over 25 kg.', 'Pack sizes', true],
        ['Week 4', 'Easier getting up', 'Joint support builds with daily use.', 'Draft wording', false],
        [
          'Week 8+',
          'Daily habit for joint-prone breeds',
          'Made for seniors and breeds prone to joint issues.',
          'Mobility+ FAQ',
          true,
        ],
      ],
      made: {
        title: 'Joint actives, measured per sachet.',
        sub: 'Measured per sachet, so the dose is right without a scoop.',
        call: [
          ['Glucosamine 1,050 mg', 'shellfish-sourced, per the label'],
          ['MSM 815 mg', 'joint comfort'],
          ['Collagen I, II, III', 'cartilage and tendons'],
          ['Chicken bone broth', 'the base, so dogs eat it'],
        ],
        src: 'Mobility+ ingredient panel',
      },
    },
    dogmeal: {
      label: 'Fresh dog meal',
      meet: [
        ['food', 'Human-grade, chef-made recipes', 'Meals FAQ'],
        ['tummy', 'No added fat or salt', 'Meals FAQ'],
        ['food', 'No preservatives: sealed and steam-cooked', 'Meals FAQ'],
      ],
      expect: [
        [
          'Day 1–5',
          'Switch over gradually',
          '20% new food on day 1, up to 100% on day 5.',
          'Meals FAQ',
          true,
        ],
        [
          'Week 2',
          'Settled on the new food',
          'Watch stools; see a vet if loose stools last over 24 h.',
          'Meals FAQ',
          true,
        ],
        ['Week 4', 'Coat and energy', 'Fresh food with organ meat and healthy fats.', 'Draft wording', false],
      ],
      made: {
        title: 'Cooked in the pack, so nothing else is needed.',
        sub: 'Retort cooking seals the meal airtight and keeps it fresh without preservatives.',
        call: [
          ['Human-grade ingredients', 'cooked in a kitchen'],
          ['Retort-sealed', 'airtight, cooked in the pack'],
          ['18-month shelf life', 'unopened, no fridge needed'],
          ['Use within 24 h', 'once opened, keep it in the fridge'],
        ],
        src: 'Meals FAQ on goofytails.com',
      },
    },
    catmeal: {
      label: 'Wet cat food',
      meet: [
        ['hydration', 'Moisture with every meal', 'Wet food (product page)'],
        ['food', 'Taurine for heart and eye health', 'Cat meals FAQ'],
        ['food', 'Grain-free, no preservatives', 'Cat meals FAQ'],
      ],
      expect: [
        [
          'Day 1–7',
          'Mix it in slowly',
          'Cats notice small changes; introduce over about a week.',
          'Cat meals FAQ',
          true,
        ],
        ['Week 2', 'Finished bowls', 'Picky eaters are named on the recipe pages.', 'Product pages', true],
        [
          'Week 4',
          'More water every day',
          'Cats get most of their water from food.',
          'Brand vet quote',
          true,
        ],
      ],
      made: {
        title: 'Sealed and cooked in the pouch.',
        sub: 'That is why there are no preservatives in the ingredient list.',
        call: [
          ['Real fish or chicken', 'first ingredient on the label'],
          ['Taurine added', 'essential for cats'],
          ['Retort-sealed', 'cooked in the pouch'],
          ['Room temperature', 'cats prefer it that way'],
        ],
        src: 'Cat meals FAQ',
      },
    },
    fd: {
      label: 'Freeze-dried treat',
      meet: [
        ['food', 'One ingredient, nothing added', 'Treats FAQ'],
        ['fussy', 'High-value reward for training', 'Treats FAQ'],
        ['food', 'Freeze-dried, so nutrients are kept', 'Treats FAQ'],
      ],
      expect: [
        ['Day 1', 'A reward they will work for', 'Small, strong-smelling pieces.', 'Treats FAQ', true],
        [
          'Daily',
          'A few pieces, or crumbled over food',
          'Follow the per-weight serving.',
          'Feeding guide',
          true,
        ],
      ],
      made: {
        title: 'Freeze-dried, not baked.',
        sub: 'Water is removed without heat, so flavour and nutrients stay.',
        call: [
          ['Single ingredient', 'nothing else added'],
          ['No heat', 'freeze-drying, not cooking'],
          ['No preservatives', 'dry enough to keep'],
          ['Crunchy', 'crumble over food or serve whole'],
        ],
        src: 'Freeze-dried treat FAQs',
      },
    },
    dental: {
      label: 'Dental chew',
      meet: [
        ['dental', 'Cleans teeth as your dog chews', 'Dental sticks description'],
        ['dental', 'Freshens breath', 'Product page'],
        ['food', 'One a day by weight', 'Feeding guide'],
      ],
      expect: [
        ['Day 1', 'A daily chew', '1–4 sticks a day by weight.', 'Feeding guide', true],
        ['Week 2', 'Fresher breath', 'Daily chewing helps keep teeth clean.', 'Draft wording', false],
      ],
      made: null,
    },
    omega: {
      label: 'Plant-based omega oil',
      meet: [
        ['coat', 'Essential fatty acids for skin and coat', 'Omega oil FAQ'],
        ['joints', 'Supports joints and mobility', 'Omega oil FAQ'],
        ['food', 'Hemp seed oil, no CBD', 'Omega oil FAQ'],
      ],
      expect: [
        ['Day 1', 'A few drops over food', 'Your vet sets the amount.', 'Omega oil FAQ', true],
        ['Week 4+', 'Softer coat', 'Fatty acids support skin and coat.', 'Draft wording', false],
      ],
      made: null,
    },
    paw: {
      label: 'Paw balm',
      meet: [
        ['coat', 'Hemp butter for dry pads', 'Product page'],
        ['coat', 'Apply directly to clean pads', 'Product page'],
      ],
      expect: [],
      made: null,
    },
  };
  function sciFor(id) {
    let f = SCI_FAM[id];
    if (!f) return null;
    let s = SCIENCE[f];
    if (s && s.alias) s = SCIENCE[s.alias];
    return s;
  }
  const showDraft = () => !GTQ.shopify || GTQ.showDrafts;

  /* ============================================================================
     SECTION 13 — WHATSAPP UNLOCK + LEADS
     renderStory() = plan reveal + "Unlock 25%" screen. waCheck() validates +91 numbers.
     sendLead() fires gtq:lead and POSTs JSON to leadEndpoint (WhatsApp tool webhook).
     If the webhook rejects browser requests (CORS), change the fetch here.
     ============================================================================ */
  /* Story screens: shown once, after "Building plan" and before the full plan. */
  let story = -1; // -1 = not started; 0 reveal, 1 unlock; 9 = done
  const availPick = (r) => r.picks.find((id) => !r.soldOut.has(id));
  function storyScreens(r) {
    const out = [];
    if (sciFor(r.picks[0])) out.push('reveal');
    if (GTQ.unlock !== false) out.push('unlock');
    return out;
  }
  function effectsFor(id) {
    const S = sciFor(id);
    return S ? S.meet.map((m) => m[1]) : [];
  }
  function expectFor(id) {
    const S = sciFor(id);
    return S && S.expect ? S.expect.filter((e) => e[4] || GTQ.showDrafts) : [];
  }
  function renderStory() {
    const r = recommend(A);
    if (!r.picks.length) {
      story = 9;
      renderResult();
      return;
    }
    const list = storyScreens(r);
    if (story >= list.length) {
      story = 9;
      renderResult();
      return;
    }
    const kind = list[story],
      id = r.picks[0],
      so = r.soldOut.has(id);
    const back = `<button class="back sback" id="sback" aria-label="Back">${ic('back')}<span>Back</span></button>`;
    let body = '';
    if (kind === 'reveal') {
      const ex = expectFor(id).slice(0, 3);
      body = `<section class="story reveal enter">${back}
     <p class="kicker">${Nm()}'s plan is ready</p>
     <h1 tabindex="-1">${esc(shortName(id))}</h1>
     <p class="for">For ${(A.concerns.length ? A.concerns : ['food'])
       .map((c) => CONCERNS[c].short)
       .slice(0, 2)
       .join(' and ')}${so ? ' · <span class="so">Sold out right now</span>' : ''}</p>
     ${so ? `<p class="for">We'll message you on WhatsApp when it's back. Your plan has what's in stock today.</p>` : ''}
     <div class="hero-shot"><img src="${liveImgs(id)[0]}" alt="${esc(liveTitle(id))}" onerror="this.remove()"></div>
     <h2 class="h4">What it does for ${Nm()}</h2>
     <ul class="outs">${effectsFor(id)
       .map((t, k) => `<li>${ic('check')}<span>${esc(t)}</span></li>`)
       .join('')}</ul>
     ${ex.length ? `<h2 class="h4">What you'll notice</h2><ol class="tl">${ex.map(([when, t]) => `<li><span class="when">${esc(when)}</span><b>${esc(t)}</b></li>`).join('')}</ol>` : ''}
     ${DISC('on-dark')}
     <div class="story-cta"><button class="cta light" id="snext">${list.includes('unlock') ? 'Continue' : `See ${Nm()}'s plan`}</button></div></section>`;
    }
    if (kind === 'unlock') {
      const pct = GTQ.unlockPct || 25;
      body = `<section class="story unlock enter">${back}
     <p class="kicker">Before you see the plan</p><h1 tabindex="-1">Unlock ${pct}% off ${Nm()}'s plan</h1>
     <p class="lede">Get the plan and your code on WhatsApp.</p>
     <form id="wa" novalidate>
      <label for="wa-num">WhatsApp number</label>
      <div class="wa-row"><span class="cc">+91</span><input id="wa-num" type="tel" inputmode="numeric" autocomplete="tel-national" maxlength="16" placeholder="98765 43210" aria-describedby="wa-err"></div>
      <p class="err" id="wa-err" role="alert" hidden>Enter a 10-digit mobile number starting with 6, 7, 8 or 9.</p>
      <label class="consent"><input type="checkbox" id="wa-ok"> <span>Send my plan and offers on WhatsApp. Reply STOP anytime.</span></label>
      <p class="err" id="wa-err2" role="alert" hidden>Tick the box so we can message you.</p>
      <button class="cta" type="submit">Unlock ${pct}% off</button>
     </form>
     <button class="link skip" id="snext">No thanks, show my plan</button>
     ${DISC()}</section>`;
    }
    root.classList.remove('is-res');
    HOST().classList.remove('has-bar');
    root.innerHTML = body;
    window.scrollTo({ top: 0 });
    const h = root.querySelector('h1');
    if (h) h.focus({ preventScroll: true });
    root.querySelector('#sback').onclick = () => {
      if (story === 0) {
        story = -1;
        dirn = -1;
        step = STEPS.length - 1;
        render();
        dirn = 1;
      } else {
        story--;
        renderStory();
      }
    };
    root.querySelector('#snext').onclick = () => {
      story++;
      renderStory();
    };
    const f = root.querySelector('#wa');
    if (f)
      f.onsubmit = (e) => {
        e.preventDefault();
        const ph = waCheck(
          root.querySelector('#wa-num'),
          root.querySelector('#wa-ok'),
          root.querySelector('#wa-err'),
          root.querySelector('#wa-err2'),
        );
        if (!ph) return;
        const lead = {
          type: 'unlock',
          phone: ph,
          consent: true,
          pet: A.name || A.species,
          species: A.species,
          breed: A.breed || '',
          breedNotListed: !!A.breedCustom,
          concerns: A.concerns,
          other: A.other || {},
          plan: r.picks,
        };
        sendLead(lead);
        if (GTQ.shopify && GTQ.discountCode)
          fetch('/discount/' + encodeURIComponent(GTQ.discountCode), { credentials: 'same-origin' }).catch(
            () => {},
          );
        A.unlocked = true;
        A.waGiven = true;
        save();
        story++;
        renderStory();
      };
  }
  /* phone check shared by unlock + notify-me. Returns +91… or null. The number is never stored. */
  function waCheck(inp, box, e1, e2) {
    const n = inp.value.replace(/\D/g, '').replace(/^(?:91|0)(?=\d{10}$)/, '');
    e1.hidden = /^[6-9]\d{9}$/.test(n);
    if (e2) e2.hidden = !box || box.checked;
    if (!e1.hidden) {
      inp.focus();
      return null;
    }
    if (box && !box.checked) return null;
    return '+91' + n;
  }
  function sendLead(lead) {
    if (!A.leadId) A.leadId = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    lead.leadId = A.leadId;
    save();
    document.dispatchEvent(new CustomEvent('gtq:lead', { detail: lead }));
    if (GTQ.leadEndpoint)
      fetch(GTQ.leadEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(lead),
      }).catch(() => {});
    if (GTQ.netlifyForm) {
      const f = {
        'form-name': GTQ.netlifyForm,
        type: lead.type || '',
        phone: lead.phone || '',
        consent: lead.consent ? 'yes' : '',
        pet: lead.pet || '',
        species: lead.species || A.species || '',
        breed: lead.breed || A.breed || '',
        breed_not_listed: lead.breedNotListed || A.breedCustom ? 'yes' : '',
        concerns: (lead.concerns || A.concerns || []).join(', '),
        plan: (lead.plan || []).map((id) => (P[id] ? P[id].h : id)).join(', '),
        product: lead.product || '',
        other: Object.entries(A.other || {})
          .filter((x) => x[1])
          .map(([k, v]) => k + ': ' + v)
          .join(' | '),
      };
      fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(f).toString(),
      }).catch(() => {});
    }
  }
  function shortName(id) {
    return liveTitle(id)
      .replace(/\s+(Supplement|Wellness|Hydration|Gluten|Wet Cat|Fresh Food|for (Dogs|Cats)).*$/, '')
      .replace(/\s*\+\s*$/, '+')
      .trim();
  }
  const NOTIFIED = new Set();

  /* ============================================================================
     SECTION 14 — RESULTS PAGE, BUNDLE TOTAL, ADD TO CART
     Builds product cards, "Also good for", bundle total (minus unlock %), free-delivery
     bar and the Add-to-cart buttons. addAll() further down does the actual cart add.
     ============================================================================ */
  function renderResult(stay) {
    const y0 = window.scrollY;
    if (!FEED) FEED = A.concerns[0] === 'food' && A.diet !== 'kibble' ? 'full' : 'topper';
    const r = recommend(A);
    const sp = A.species;
    const allergyFlags = A.flags.filter((f) =>
      ['chicken', 'fish', 'egg', 'dairy', 'gluten', 'grain', 'soy'].includes(f),
    );
    const items = r.picks
      .map((id, i) => {
        const off = SKIP.has(id),
          so = r.soldOut.has(id);
        const p = P[id];
        const d = doseLine(id, A);
        const rs = r.reasons[id] || [];
        const v = variantFor(id);
        const forWhat = rs.filter((x) => x.rank != null).sort((x, y) => x.rank - y.rank)[0];
        const feed = p.meal && !p.topper && sp === 'dog' && A.ageMonths >= 3 && !so;
        const note =
          sp === 'cat' && id === 'cVital' && A.concerns.includes('joints')
            ? `<p class="note-in">There's no Mobility+ for cats yet. Vitality+ is our closest match for joints, with collagen and boswellia.</p>`
            : '';
        return `<article class="item${i === 0 ? ' pick1' : ''}${off ? ' off' : ''}${so ? ' sold' : ''}" style="--i:${i}">
      ${shot(id)}
      <div class="head"><p class="role">${forWhat ? 'For ' + forWhat.c : 'For ' + Nm()}</p><h3>${esc(shortName(id))}</h3>
       <p class="var">${so ? '<span class="so">Sold out</span>' : `${v.unit ? esc(v.unit) + ' · ' : ''}${priceLine(v)}`}</p></div>
      ${note}
      ${feed ? `<div class="mode feedm" role="group" aria-label="How ${nm()} will eat it"><button data-feed="full" aria-pressed="${FEED === 'full'}">Full meal</button><button data-feed="topper" aria-pressed="${FEED === 'topper'}">As a topper</button></div>` : ''}
      <div class="dose">${d.txt}</div>
      <ul class="eff">${effectsFor(id)
        .slice(0, 2)
        .map((t) => `<li>${ic('check', 'ic sm')}${esc(t)}</li>`)
        .join('')}</ul>
      ${(allergyFlags.length || (A.other && A.other.flag)) && p.ing ? `<details class="more ingd"><summary>Ingredients</summary><p class="ing">${esc(p.ing)}</p></details>` : ''}
      <div class="acts">${so ? (NOTIFIED.has(id) ? `<span class="notified">${ic('check', 'ic sm')} We'll message you when it's back</span>` : `<button class="tog on" data-notify="${id}">Notify me when it's back</button>`) : `<a href="${SITE + p.h}" target="_blank" rel="noopener">View product ${ic('ext', 'ic sm')}</a>${i > 0 ? `<button class="tog" data-skip="${id}" aria-pressed="${!off}">${off ? 'Add back' : 'Remove'}</button>` : ''}`}</div>
      <div class="notify" id="nf-${id}" hidden><label for="nf-num-${id}">WhatsApp number</label><div class="wa-row"><span class="cc">+91</span><input id="nf-num-${id}" type="tel" inputmode="numeric" maxlength="16" placeholder="98765 43210"></div><p class="err" id="nf-err-${id}" role="alert" hidden>Enter a 10-digit mobile number starting with 6, 7, 8 or 9.</p><label class="consent"><input type="checkbox" id="nf-ok-${id}"> <span>Message me on WhatsApp when it's back.</span></label><p class="err" id="nf-err2-${id}" role="alert" hidden>Tick the box so we can message you.</p><button class="cta" data-nfgo="${id}">Notify me</button></div>
    </article>`;
      })
      .join('');
    const inPlan = r.picks
      .filter((id) => !SKIP.has(id) && !r.soldOut.has(id))
      .concat(r.extras.map((x) => x[0]).filter((id) => ADDED.has(id)));
    const total = inPlan.reduce((s, id) => s + variantFor(id).price, 0);
    const mrpTotal = inPlan.reduce((s, id) => {
      const v = variantFor(id);
      return s + (v.mrp || v.price);
    }, 0);
    const PCT = A.unlocked ? GTQ.unlockPct || 25 : 0,
      disc = Math.round((total * PCT) / 100),
      pay = total - disc;
    const FREE = GTQ.freeShipping || 700,
      toFree = Math.max(0, FREE - pay);
    const concernTxt = (A.concerns.length ? A.concerns : ['food']).map((c) => CONCERNS[c].short);
    const title = r.picks.length
      ? `${Nm()}'s <em>${concernTxt.slice(0, 2).join(' and ')}</em> plan`
      : `Let's get ${nm()} <em>a vet's view first</em>`;
    const summary = [
      sp === 'dog' ? 'Dog' : 'Cat',
      ageLabel(A.ageMonths),
      A.breed && A.breed,
      `~${Math.round(A.kg)} kg${A.kgFromBreed ? '*' : ''}`,
    ]
      .filter(Boolean)
      .join(' · ');
    const vetNotes = r.notes.filter(([t]) => t !== 'Allergies');
    const O = A.other || {};
    if (O.flag)
      vetNotes.push([
        'You told us',
        `"${esc(O.flag)}". We can't check this one automatically, so each product shows its ingredients. Check them, or ask us on WhatsApp before ordering.`,
      ]);
    if (O.concern)
      vetNotes.push([
        'Also noted',
        `"${esc(O.concern)}". Our team will look at this and can suggest more on WhatsApp.`,
      ]);
    const stack = inPlan
      .slice(0, 4)
      .map(
        (id, k) =>
          `<span class="st" style="--k:${k}"><img src="${liveImgs(id)[0]}" alt="" onerror="this.parentNode.classList.add('noimg');this.remove()"><b>${esc(P[id].short)}</b></span>`,
      )
      .join('');

    root.innerHTML = `<div class="res enter">
   <div class="prog"><button class="back" id="back">${ic('back')}<span>Edit answers</span></button><span class="prog-txt">${esc(summary)}</span></div>
   ${A.sample ? `<div class="banner info"><b>Sample plan.</b> <button class="link" id="mine">Start the quiz for your pet</button></div>` : ''}
   <header class="rhead">${petPhoto()}<div class="rh-txt"><p class="kicker">Made for ${Nm()}</p><h1 tabindex="-1">${title}</h1></div></header>
   ${A.kgFromBreed ? `<p class="wnote">*Weight is indicative, based on a typical adult ${esc(A.breed)}. Amounts follow the weight you chose.</p>` : ''}
   ${allergyFlags.length ? `<p class="checked">${ic('check', 'ic sm')} Every product checked for ${allergyFlags.map((f) => ({ chicken: 'chicken', fish: 'fish and seafood', egg: 'egg', dairy: 'dairy', gluten: 'gluten and wheat', grain: 'grains', soy: 'soya' })[f]).join(', ')}</p>` : ''}
   ${vetNotes.map(([t, m]) => `<div class="banner"><b>${t}.</b> ${m}</div>`).join('')}
   ${
     /* Vet consult card (17 Oct): shown high up, right under the health notes, whenever a vet should be involved */
     r.needVet
       ? (() => {
           const vv = variantFor('vet');
           return `<section class="vetcard" aria-label="Talk to a vet">
     <div><p class="kicker">Recommended for ${Nm()}</p>
     <h2 class="h4">Talk to a vet online first</h2>
     <p>Book an online consultation and get advice on what is safe for ${nm()} right now.
     <b>${inr(vv.price)}</b>${vv.mrp ? ` <s>${inr(vv.mrp)}</s>` : ''}</p></div>
     <a class="cta" href="${SITE + P.vet.h}" target="_blank" rel="noopener">Book a vet consult · ${inr(vv.price)}</a>
   </section>`;
         })()
       : ''
   }
   ${
     r.picks.length
       ? `
   <div class="mode" role="group" aria-label="Pack size"><button data-m="try" aria-pressed="${mode === 'try'}">Try first</button><button data-m="stock" aria-pressed="${mode === 'stock'}">Value pack</button></div>
   <div class="items">${items}</div>
   ${
     r.extras.length
       ? `<section class="sec"><h2>Also good for ${nm()}</h2><div class="extras">${r.extras
           .map(([id, why]) => {
             const v = variantFor(id),
               on = ADDED.has(id);
             return `<div class="extra${on ? ' on' : ''}">${shot(id, 'shot sm')}<div class="ex-txt"><h3 class="h4">${esc(shortName(id))}</h3><p>${esc(why)}</p><div class="acts">${priceLine(v)}<button class="tog${on ? ' on' : ''}" data-add="${id}" aria-pressed="${on}">${on ? ic('check', 'ic sm') + 'Added' : ic('plus', 'ic sm') + 'Add'}</button></div></div></div>`;
           })
           .join('')}</div></section>`
       : ''
   }
   ${
     inPlan.length
       ? `<section class="bundle" id="bundle" aria-labelledby="bh">
     <div class="bstack" aria-hidden="true">${stack}</div>
     <h2 id="bh">${Nm()}'s bundle</h2>
     <ul class="blines">${inPlan
       .map((id) => {
         const v = variantFor(id);
         return `<li><span>${esc(shortName(id))}<small>${esc(v.unit || '')}</small></span><span class="num">${inr(v.price)}</span></li>`;
       })
       .join('')}</ul>
     ${PCT ? `<div class="bdisc"><span>WhatsApp offer · ${PCT}% off</span><span class="num">−${inr(disc)}</span></div>` : ''}
     <div class="btotal"><span>${inPlan.length} item${inPlan.length === 1 ? '' : 's'}</span><span class="num">${mrpTotal > pay ? `<s>${inr(mrpTotal)}</s>` : ''}<b>${inr(pay)}</b></span></div>
     <div class="mile">${ic('truck')}<div><div class="mbar"><i style="width:${Math.min(100, (pay / FREE) * 100)}%"></i></div><p>${toFree ? `${inr(toFree)} more for free delivery` : 'Free delivery on this order'}</p></div></div>
     <button class="cta" id="addall">Add bundle to cart · ${inr(pay)}</button>
     <p class="toast" id="toast" role="status" hidden>In the live store this adds all ${inPlan.length} items to the cart in one tap.</p>
   </section>`
       : ''
   }`
       : Object.keys(r.askFirst).length
         ? `<div class="banner"><b>Nothing we can confirm yet.</b> Write to care@goofytails.com or call +91 96255 62135 and we'll check which recipes suit ${nm()}.</div>`
         : `<div class="banner"><b>Nothing from us for this one yet.</b> A vet is the right first step for ${nm()}.</div>`
   }
   ${(() => {
     const af = Object.entries(r.askFirst);
     return af.length
       ? `<section class="sec"><h2>Ask us first</h2><div class="excl">${af.map(([id, why]) => `<div class="ask"><b>${esc(shortName(id))}</b><span>${esc(why)} · <a href="mailto:care@goofytails.com?subject=${encodeURIComponent('Pet plan: ' + shortName(id))}">Ask us</a></span></div>`).join('')}</div></section>`
       : '';
   })()}
   ${DISC('res-disc')}
   <div class="endrow"><button class="cta ghost" id="other">Add a ${sp === 'dog' ? 'cat' : 'dog'}</button><button class="cta ghost" id="restart">Start over</button></div>
   ${
     inPlan.length
       ? `<div class="actionbar cart"><div class="wrap ab-in"><a class="ab-stack" href="#bundle" aria-label="View the bundle">${inPlan
           .slice(0, 3)
           .map(
             (id, k) =>
               `<span style="--k:${k}"><img src="${liveImgs(id)[0]}" alt="" onerror="this.remove()"></span>`,
           )
           .join(
             '',
           )}</a><span class="ab-note"><b>${inr(pay)}</b> · ${inPlan.length} item${inPlan.length === 1 ? '' : 's'}</span><button class="cta" id="addall2">Add to cart</button></div></div>`
       : ''
   }
  </div>`;
    HOST().classList.toggle('has-bar', !!inPlan.length);
    root.classList.add('is-res');
    root.querySelector('#back').onclick = () => {
      dirn = -1;
      step = STEPS.length - 1;
      render();
      dirn = 1;
    };
    root.querySelectorAll('[data-m]').forEach(
      (b) =>
        (b.onclick = () => {
          mode = b.dataset.m;
          renderResult(true);
        }),
    );
    root.querySelectorAll('[data-add]').forEach(
      (b) =>
        (b.onclick = () => {
          const id = b.dataset.add;
          ADDED.has(id) ? ADDED.delete(id) : ADDED.add(id);
          renderResult(true);
        }),
    );
    root.querySelectorAll('[data-feed]').forEach(
      (b) =>
        (b.onclick = () => {
          FEED = b.dataset.feed;
          renderResult(true);
        }),
    );
    root.querySelectorAll('[data-skip]').forEach(
      (b) =>
        (b.onclick = () => {
          const id = b.dataset.skip;
          SKIP.has(id) ? SKIP.delete(id) : SKIP.add(id);
          renderResult(true);
        }),
    );
    root.querySelectorAll('[data-shot]').forEach(
      (b) =>
        (b.onclick = () => {
          const im = liveImgs(b.dataset.shot);
          if (im.length < 2) return;
          const i = (+b.dataset.i + 1) % im.length;
          b.dataset.i = i;
          const g = b.querySelector('img');
          if (g) g.src = im[i];
          b.querySelectorAll('.dots i').forEach((d, k) => d.classList.toggle('on', k === i));
        }),
    );
    root.querySelectorAll('[data-notify]').forEach(
      (b) =>
        (b.onclick = () => {
          const id = b.dataset.notify;
          if (A.waGiven) {
            sendLead({ type: 'back-in-stock', product: P[id].h, pet: A.name || A.species });
            NOTIFIED.add(id);
            renderResult(true);
            return;
          }
          const box = root.querySelector('#nf-' + id);
          box.hidden = false;
          b.hidden = true;
          root.querySelector('#nf-num-' + id).focus();
        }),
    );
    root.querySelectorAll('[data-nfgo]').forEach(
      (b) =>
        (b.onclick = () => {
          const id = b.dataset.nfgo;
          const ph = waCheck(
            root.querySelector('#nf-num-' + id),
            root.querySelector('#nf-ok-' + id),
            root.querySelector('#nf-err-' + id),
            root.querySelector('#nf-err2-' + id),
          );
          if (!ph) return;
          sendLead({
            type: 'back-in-stock',
            phone: ph,
            consent: true,
            product: P[id].h,
            pet: A.name || A.species,
          });
          NOTIFIED.add(id);
          renderResult(true);
        }),
    );
    /* ---- addAll(): adds every in-plan item via /cart/add.js, sets the "Pet plan"
       cart attribute, fires gtq:added, then either opens the theme cart drawer
       (#HeaderCartTrigger, when "Stay on page" is ON) or goes to /cart. ---- */
    /* ---- Cart sync (17 Oct): every quiz item in the cart carries the hidden tag _source=pet-plan-quiz.
       The button reads "Update cart" when quiz items are already in the cart, "Add bundle to cart" when not
       (e.g. the customer emptied the cart in the GoKwik drawer). ---- */
    const isQuizLine = (it) => it.properties && it.properties._source === 'pet-plan-quiz';
    // Compare the quiz items in the cart with the plan on screen:
    //   none -> "Add bundle to cart"   same -> "Added to cart ✓"   different -> "Update cart"
    const planKey = () =>
      inPlan
        .map((id) => variantFor(id).vid)
        .filter(Boolean)
        .map(String)
        .sort()
        .join(',');
    const setCartLabels = (state) => {
      const a = root.querySelector('#addall'),
        a2 = root.querySelector('#addall2');
      const big = { none: 'Add bundle to cart · ' + inr(pay), same: 'Added to cart ✓', diff: 'Update cart · ' + inr(pay) };
      const small = { none: 'Add to cart', same: 'Added ✓', diff: 'Update cart' };
      if (a) a.innerHTML = big[state];
      if (a2) a2.innerHTML = small[state];
    };
    const refreshCartLabels = () => {
      if (!GTQ.shopify) return;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((c) => {
          const lines = c.items.filter(isQuizLine);
          if (!lines.length) return setCartLabels('none');
          const cartKey = lines
            .flatMap((l) => Array(l.quantity).fill(String(l.variant_id)))
            .sort()
            .join(',');
          setCartLabels(cartKey === planKey() ? 'same' : 'diff');
        })
        .catch(() => {});
    };
    cartLabelCheck = refreshCartLabels; // used by the page-wide listener at the bottom of this file
    refreshCartLabels();

    const addAll = () => {
      const bs = ['#addall', '#addall2'].map((x) => root.querySelector(x)).filter(Boolean);
      if (GTQ.permalink) {
        const ids = inPlan.map((id) => variantFor(id).vid).filter(Boolean);
        if (!ids.length) return;
        bs.forEach((b) => {
          b.disabled = true;
          b.innerHTML = 'Opening cart…';
        });
        const q = new URLSearchParams({ ref: 'pet-plan-quiz' });
        if (A.unlocked && GTQ.discountCode) q.set('discount', GTQ.discountCode);
        q.set('attributes[Pet plan]', `${A.name || A.species} · ${A.concerns.join(', ')}`);
        location.href =
          GTQ.permalink.replace(/\/$/, '') +
          '/cart/' +
          ids.map((v) => v + ':1').join(',') +
          '?' +
          q.toString();
        return;
      }
      if (GTQ.shopify) {
        bs.forEach((b) => {
          b.disabled = true;
          b.innerHTML = 'Adding…';
        });
        const items = inPlan
          .map((id) => ({
            id: variantFor(id).vid,
            quantity: 1,
            properties: { _source: 'pet-plan-quiz', _pet: A.name || A.species },
          }))
          .filter((x) => x.id);
        // 1) read the cart, 2) remove the quiz's earlier items, 3) add the current plan.
        //    So pressing the button again REPLACES the bundle instead of doubling quantities.
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((cart) => {
            const updates = {};
            cart.items.filter(isQuizLine).forEach((it) => (updates[it.key] = 0));
            if (!Object.keys(updates).length) return null;
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ updates }),
            });
          })
          .then(() =>
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        }))
          .then((r) => {
            if (!r.ok) throw r;
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                attributes: { 'Pet plan': `${A.name || A.species} · ${A.concerns.join(', ')}` },
              }),
            });
          })
          .then(() => {
            document.dispatchEvent(new CustomEvent('gtq:added', { detail: { items } }));
            if (!GTQ.stayOnPage) {
              location.href = GTQ.cartUrl || '/cart';
              return;
            }
            // Open the cart drawer the same way a customer would: GoKwik's slide cart listens to the header cart icon.
            const ct = document.getElementById('HeaderCartTrigger');
            if (ct) ct.click();
            else document.dispatchEvent(new CustomEvent('cart:open'));
            // Opening the drawer this way can leave the page scroll-locked after the drawer is closed
            // (theme popup + GoKwik both lock scrolling). unlockScrollWhenDrawerCloses() fixes that.
            unlockScrollWhenDrawerCloses();
            bs.forEach((b) => (b.disabled = false));
            setCartLabels('same'); // "Added to cart ✓" until the cart or the plan changes
          })
          .catch(() => {
            bs.forEach((b) => {
              b.disabled = false;
              b.innerHTML = 'Try again';
            });
            const t = root.querySelector('#toast');
            if (t) {
              t.hidden = false;
              t.textContent = 'Something went wrong adding to cart. Please try again.';
            }
          });
        return;
      }
      bs.forEach((b) => {
        b.disabled = true;
        b.dataset.l = b.innerHTML;
        b.innerHTML = 'Adding…';
      });
      setTimeout(
        () => {
          bs.forEach((b) => {
            b.innerHTML = ic('check', 'ic sm') + ' Added to cart';
            b.classList.add('done');
          });
          const t = root.querySelector('#toast');
          if (t) t.hidden = false;
          setTimeout(
            () =>
              bs.forEach((b) => {
                b.disabled = false;
                b.innerHTML = b.dataset.l;
                b.classList.remove('done');
              }),
            2600,
          );
        },
        RM ? 0 : 650,
      );
    };
    ['#addall', '#addall2'].forEach((s) => {
      const b = root.querySelector(s);
      if (b) b.onclick = addAll;
    });
    const mine = root.querySelector('#mine');
    if (mine) mine.onclick = () => root.querySelector('#restart').click();
    root.querySelector('#restart').onclick = () => {
      story = -1;
      SKIP.clear();
      ADDED.clear();
      FEED = null;
      A = {
        species: null,
        name: '',
        ageMonths: null,
        breed: '',
        band: null,
        kg: null,
        kgFromBreed: false,
        diet: null,
        activity: null,
        concerns: [],
        flags: [],
        other: {},
      };
      step = 0;
      render();
    };
    root.querySelector('#other').onclick = () => {
      story = -1;
      SKIP.clear();
      ADDED.clear();
      FEED = null;
      const s = sp === 'dog' ? 'cat' : 'dog';
      A = {
        species: s,
        name: '',
        ageMonths: null,
        breed: '',
        band: null,
        kg: null,
        kgFromBreed: false,
        diet: null,
        activity: null,
        concerns: [],
        flags: [],
        other: {},
      };
      step = 1;
      render();
    };
    if (stay) {
      root.querySelector('.res').classList.remove('enter');
      window.scrollTo({ top: y0 });
    } else if (started) {
      const h = root.querySelector('h1');
      if (h) h.focus({ preventScroll: true });
    }
    started = true;
    save();
  }

  /* ============================================================================
     SECTION 15 — SAMPLE PLANS, SAVE / RESUME, START-UP
     Answers are saved in localStorage key "gt-quiz" so a refresh resumes the quiz.
     Never store the phone number here.
     ============================================================================ */
  /* ---- unlockScrollWhenDrawerCloses(): keeps the page scrollable after the cart drawer closes.
     Starts on the first add-to-cart and then checks every 0.4 s:
       • a drawer/overlay is covering the page  -> let it lock scrolling (remove our override)
       • nothing covers the page but scrolling is still locked -> force scrolling back on
     "Covered" = the element at the left-middle or centre of the screen sits inside a
     position:fixed layer (drawers and their dark overlays are fixed). Our sticky bar and
     the site header are ignored. ---- */
  let scrollGuard = null;
  function unlockScrollWhenDrawerCloses() {
    if (scrollGuard) return;
    const html = document.documentElement,
      body = document.body;
    let forced = false;
    const fixedAt = (x, y) => {
      let el = document.elementFromPoint(x, y);
      while (el && el !== body && el !== html) {
        if (el.closest && el.closest('.gtq .actionbar, header, .site-header')) return false;
        if (getComputedStyle(el).position === 'fixed') return true;
        el = el.parentElement;
      }
      return false;
    };
    const covered = () =>
      fixedAt(12, Math.round(innerHeight * 0.55)) || fixedAt(Math.round(innerWidth / 2), Math.round(innerHeight * 0.45));
    const locked = () =>
      ['overflow', 'overflowY'].some(
        (k) => getComputedStyle(html)[k] === 'hidden' || getComputedStyle(body)[k] === 'hidden',
      ) || getComputedStyle(body).position === 'fixed';
    const force = () => {
      html.classList.remove('lock-scroll', 'cart-open', 'js-drawer-open');
      [html, body].forEach((el) => {
        el.style.setProperty('overflow', 'auto', 'important');
        el.style.setProperty('overflow-y', 'auto', 'important');
      });
      if (getComputedStyle(body).position === 'fixed') {
        const y = -parseInt(body.style.top || '0', 10) || 0;
        body.style.setProperty('position', 'static', 'important');
        body.style.top = '';
        window.scrollTo(0, y);
      }
      forced = true;
    };
    const release = () => {
      [html, body].forEach((el) => {
        el.style.removeProperty('overflow');
        el.style.removeProperty('overflow-y');
      });
      body.style.removeProperty('position');
      forced = false;
    };
    let wait = 3; // let the drawer open first
    scrollGuard = setInterval(() => {
      if (wait-- > 0) return;
      if (covered()) {
        if (forced) release(); // a drawer is open again: let it work normally
      } else if (locked()) {
        force();
      }
    }, 400);
  }

  /* Re-check the cart button label whenever the customer taps the page or comes back to the tab
     (e.g. after closing or emptying the GoKwik cart drawer). */
  let cartLabelCheck = null;
  ['click', 'focus', 'visibilitychange'].forEach((ev) =>
    (ev === 'focus' ? window : document).addEventListener(ev, () => {
      if (cartLabelCheck && root.classList.contains('is-res')) setTimeout(cartLabelCheck, 800);
    }),
  );

  function loadSample(s) {
    story = 9;
    SKIP.clear();
    ADDED.clear();
    FEED = null;
    A =
      s === 'coco'
        ? {
            species: 'dog',
            name: 'Coco',
            ageMonths: 36,
            breed: 'Indie / Indian Pariah',
            band: 'm',
            kg: 20,
            kgFromBreed: true,
            diet: 'home',
            activity: 'high',
            concerns: ['food', 'tummy'],
            flags: ['chicken'],
            sample: 1,
          }
        : s === 'dog'
          ? {
              species: 'dog',
              name: 'Bruno',
              ageMonths: 96,
              breed: 'Labrador Retriever',
              band: 'l',
              kg: 30.5,
              kgFromBreed: true,
              diet: 'kibble',
              activity: 'moderate',
              concerns: ['joints', 'coat'],
              flags: ['none'],
              sample: 1,
            }
          : {
              species: 'cat',
              name: 'Mishti',
              ageMonths: 8,
              breed: 'Persian',
              band: 'xs',
              kg: 4.5,
              kgFromBreed: true,
              diet: 'kibble',
              concerns: ['fussy', 'hydration'],
              flags: ['chicken'],
              sample: 1,
            };
    step = STEPS.length;
    render();
    window.scrollTo({ top: 0 });
  }
  function save() {
    try {
      localStorage.setItem('gt-quiz', JSON.stringify({ A, step, mode }));
    } catch (e) {}
  }
  function restore() {
    try {
      const s = JSON.parse(localStorage.getItem('gt-quiz') || 'null');
      if (s && s.A) {
        A = s.A;
        step = s.step || 0;
        mode = s.mode || 'try';
      }
    } catch (e) {}
  }

  /* tabs */
  const tabs = document.getElementById('tab-quiz')
    ? [
        ['tab-quiz', 'view-quiz'],
        ['tab-spec', 'view-spec'],
      ]
    : [];
  function show(t) {
    tabs.forEach(([b, v]) => {
      const on = b === t;
      document.getElementById(b).setAttribute('aria-selected', on);
      document.getElementById(v).hidden = !on;
    });
    try {
      localStorage.setItem('gt-quiz-tab', t);
    } catch (e) {}
  }
  tabs.forEach(([b]) => (document.getElementById(b).onclick = () => show(b)));
  if (tabs.length && location.hash === '#spec') show('tab-spec');

  {
    const L = [
      ['Made with real ingredients.', 'Nothing fake. Nothing fillers.'],
      ['Thoughtful nutrition.', 'Balanced for a longer, healthier life.'],
      ['For every kind of pet.', 'Dogs. Cats. Equal love.'],
      ['Made by pet parents.', "Because we've been there too."],
    ];
    const l = L[Math.floor(Math.random() * L.length)];
    const ft = document.getElementById('ftline');
    if (ft) ft.innerHTML = '<b>' + l[0] + '</b> ' + l[1];
  }
  restore();
  if (step === 0 && !A.species) {
    GTQ.shopify || GTQ.public ? render() : loadSample('dog');
  } else render();
})();
